import { useCallback, useEffect, useRef, useState } from "react";
import * as THREE from "three";
import {
  flattenOrbDefenseGrid,
  getOrbDefenseSpawnLocations,
  getOrbDefenseTargets,
  ORB_DEFENSE_GRID_HEIGHT,
  ORB_DEFENSE_GRID_WIDTH,
  getOrbDefenseCell,
  type OrbDefenseCell,
  type OrbDefenseGridPoint,
} from "../data/orbDefenseGrid";
import {
  createOrbDefenseWaveConfig,
  findOrbDefensePath,
  getOrbDefenseMonsterReward,
  getOrbDefenseScoreBonusEnergy,
  getOrbDefenseTimeSkipBonus,
  ORB_DEFENSE_CONTROL_EVENT,
  ORB_DEFENSE_FRAME_RATE,
  ORB_DEFENSE_INITIAL_SCORE_BONUS,
  ORB_DEFENSE_PLACEABLE_TURRET_INDEXES,
  ORB_DEFENSE_PASSIVE_ENERGY_PER_ORB,
  ORB_DEFENSE_PASSIVE_ENERGY_SECONDS,
  ORB_DEFENSE_STARTING_ENERGY,
  ORB_DEFENSE_STATE_EVENT,
  ORB_DEFENSE_TURRETS,
  ORB_DEFENSE_WAVE_SECONDS,
  sortOrbDefenseTargetsBySpawnDistance,
  type OrbDefenseControlDetail,
  type OrbDefenseHudState,
  type OrbDefenseNorbKind,
  type OrbDefenseProjection,
  type OrbDefenseTurretDefinition,
  type OrbDefenseWaveConfig,
} from "../data/orbDefenseGameplay";
import {
  AUDIO_CONTROL_EVENT,
  AUDIO_STATE_EVENT,
  audioTracks,
  defaultAudioPlayerState,
  type AudioControlDetail,
  type AudioPlayerState,
} from "../data/audioPlaylist";
import {
  advanceGalagaElapsed,
  createGalagaInputState,
  createGalagaState,
  cycleGalagaWeapon,
  fireGalagaShot,
  GALAGA_BOUNDS_X,
  GALAGA_BOUNDS_Y,
  serializeGalagaState,
  setGalagaPlayerFromPointer,
  toggleGalagaView,
  type GalagaInputState,
  type GalagaState,
} from "../data/galaga";

type TechTerrainProps = {
  reducedMotion: boolean;
  gameActive: boolean;
  galagaActive: boolean;
};

type PlaneState = {
  mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  base: Float32Array;
  opacity: number;
};

type BallState = {
  mesh: THREE.Mesh<THREE.IcosahedronGeometry, THREE.MeshBasicMaterial>;
  base: Float32Array;
};

type AudioBands = {
  bass: number;
  mid: number;
  treble: number;
  energy: number;
  peak: number;
  impact: number;
  bassImpact: number;
};

type AudioNodes = {
  context: AudioContext;
  analyser: AnalyserNode;
  data: Uint8Array<ArrayBuffer>;
  waveform: Uint8Array<ArrayBuffer>;
  master: GainNode;
  source: AudioBufferSourceNode | null;
  trackIndex: number;
  startedAt: number;
  offset: number;
  isPlaying: boolean;
  bands: AudioBands;
};

type GameSceneState = {
  world: THREE.Group;
  tileMeshes: Record<OrbDefenseCell, THREE.InstancedMesh>;
  tileInstances: Record<OrbDefenseCell, TileInstanceData[]>;
  matrixObject: THREE.Object3D;
  floorMaterial: THREE.MeshBasicMaterial;
  pathMaterial: THREE.MeshStandardMaterial;
  wallMaterial: THREE.MeshStandardMaterial;
  buildMaterial: THREE.MeshStandardMaterial;
  pedestalMaterial: THREE.MeshStandardMaterial;
  spawnGates: THREE.Mesh<THREE.TorusGeometry, THREE.MeshBasicMaterial>[];
  pedestals: THREE.Mesh<THREE.CylinderGeometry, THREE.MeshStandardMaterial>[];
  tileMaterials: THREE.Material[];
  orbMeshes: THREE.Mesh<THREE.IcosahedronGeometry, THREE.MeshStandardMaterial>[];
  orbShells: THREE.Mesh<THREE.IcosahedronGeometry, THREE.MeshBasicMaterial>[];
  enemyGroups: THREE.Group[];
  projectileMeshes: THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial>[];
  turretGroups: Map<number, THREE.Group>;
  placementPreview: THREE.Group;
  placementFootprint: THREE.Mesh<THREE.BoxGeometry, THREE.MeshBasicMaterial>;
  placementRangeCurrent: THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial>;
  placementRangeNext: THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial>;
  resourceAnchors: Record<ResourceAnchorKind, ResourceAnchor>;
  rewardParticleGeometry: THREE.SphereGeometry;
  rewardMaterials: Record<ResourceAnchorKind, THREE.MeshBasicMaterial>;
  rewardParticles: WorldRewardParticle[];
  geometries: THREE.BufferGeometry[];
  materials: THREE.Material[];
  counts: Record<OrbDefenseCell, number>;
  instanceStateKey: string;
};

type GalagaSceneState = {
  world: THREE.Group;
  player: THREE.Group;
  playerCore: THREE.Mesh<THREE.IcosahedronGeometry, THREE.MeshStandardMaterial>;
  thrusterMeshes: THREE.Mesh<THREE.ConeGeometry, THREE.MeshBasicMaterial>[];
  reticle: THREE.Group;
  starMeshes: THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial>[];
  enemyGroups: THREE.Group[];
  shotMeshes: THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial>[];
  enemyShotMeshes: THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial>[];
  explosionMeshes: THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial>[];
  title: THREE.Group;
  scoreValue: THREE.Group;
  weaponValue: THREE.Group;
  boostValue: THREE.Group;
  geometries: THREE.BufferGeometry[];
  materials: THREE.Material[];
};

type TileInstanceData = {
  cell: OrbDefenseGridPoint;
  position: { x: number; z: number };
  finalY: number;
  height: number;
  index: number;
};

type WorldBuildPhase = "splash" | "planes_flatten" | "floor_rise" | "walls_rise" | "orbs_deploy" | "enemy_spawn" | "live";

type WorldBuildTimeline = {
  phase: WorldBuildPhase;
  progress: number;
  arrival: number;
  planes: number;
  floor: number;
  walls: number;
  orbs: number;
  enemies: number;
};

type GameplayStatus = "playing" | "won" | "lost";

type GameplayTarget = {
  point: OrbDefenseGridPoint;
  destroyed: boolean;
  levelBonusGiven: number;
};

type GameplayEnemy = {
  id: number;
  position: OrbDefenseGridPoint;
  path: OrbDefenseGridPoint[];
  pathIndex: number;
  health: number;
  startingHealth: number;
  speed: number;
  spawnPoint: OrbDefenseGridPoint;
  level: number;
  kind: OrbDefenseNorbKind;
  label: string;
  energyReward: number;
  scoreReward: number;
  boss: boolean;
};

type GameplaySpawner = {
  level: number;
  config: OrbDefenseWaveConfig;
  remaining: number;
  counter: number;
};

type GameplayTurret = {
  id: number;
  definitionIndex: number;
  x: number;
  y: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
  level: number;
  range: number;
  damage: number;
  cooldown: number;
  speed: number;
  timer: number;
};

type PlacementPreviewMode = "place" | "upgrade" | "blocked";

type PlacementPreviewState = {
  mode: PlacementPreviewMode;
  valid: boolean;
  point: OrbDefenseGridPoint;
  cx: number;
  cy: number;
  w: number;
  h: number;
  currentRange: number | null;
  nextRange: number | null;
};

type GameplayShot = {
  id: number;
  from: OrbDefenseGridPoint;
  to: OrbDefenseGridPoint;
  ageFrames: number;
  lifeFrames: number;
  color: number;
};

type GameplayRewardBurst = {
  id: number;
  origin: OrbDefenseGridPoint;
  reward: number;
  ageFrames: number;
  lifeFrames: number;
  dispatched: boolean;
};

type ResourceAnchorKind = "energy" | "score";

type ResourceAnchor = {
  kind: ResourceAnchorKind;
  group: THREE.Group;
  label: THREE.Group;
  value: THREE.Group;
  shell: THREE.Mesh<THREE.IcosahedronGeometry, THREE.MeshBasicMaterial>;
  core: THREE.Mesh<THREE.IcosahedronGeometry, THREE.MeshStandardMaterial>;
  target: THREE.Vector3;
  displayedValue: number;
  valueText: string;
};

type WorldRewardParticle = {
  id: number;
  kind: ResourceAnchorKind;
  mesh: THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial>;
  start: THREE.Vector3;
  controlA: THREE.Vector3;
  controlB: THREE.Vector3;
  end: THREE.Vector3;
  bornAt: number;
  duration: number;
  delay: number;
};

type GameplayState = {
  level: number;
  processedLevel: number;
  frameAccumulator: number;
  lastElapsed: number | null;
  waveTimerFrames: number;
  passiveEnergyFrames: number;
  energy: number;
  score: number;
  scoreBonus: number;
  lastReleasedBossWave: number;
  status: GameplayStatus;
  targets: GameplayTarget[];
  enemies: GameplayEnemy[];
  spawners: GameplaySpawner[];
  turrets: GameplayTurret[];
  shots: GameplayShot[];
  rewardBursts: GameplayRewardBurst[];
  selectedTurretIndex: number;
  nextEnemyId: number;
  nextTurretId: number;
  nextShotId: number;
  nextRewardBurstId: number;
  spawnCursor: number;
  message: string;
  lastPointer: OrbDefenseGridPoint | null;
  hoverPreview: PlacementPreviewState | null;
};

type RuntimeState = {
  gameActive: boolean;
  galagaActive: boolean;
  transitionStartedAt: number;
  galagaStartedAt: number;
  manualElapsed: number | null;
  zoom: number;
  zoomTarget: number;
  yaw: number;
  yawTarget: number;
  pitch: number;
  pitchTarget: number;
  focusX: number;
  focusTargetX: number;
  focusZ: number;
  focusTargetZ: number;
  projection: OrbDefenseProjection | "free";
  lastHudPublishAt: number;
  snapshot: Record<string, unknown>;
};

type WebglMode = "pending" | "ready" | "static" | "unsupported";

type TerrainDeviceProfile = {
  reducedMotion: boolean;
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
  innerWidth: number;
  innerHeight: number;
  devicePixelRatio: number;
};

declare global {
  interface Window {
    advanceTime?: (ms: number) => void;
    render_game_to_text?: () => string;
    render_galaga_to_text?: () => string;
    webkitAudioContext?: typeof AudioContext;
  }
}

const GRID_CELLS = flattenOrbDefenseGrid();
const SPAWN_LOCATIONS = getOrbDefenseSpawnLocations();
const ORB_TARGETS = sortOrbDefenseTargetsBySpawnDistance(getOrbDefenseTargets(), SPAWN_LOCATIONS);
const GRID_COUNTS = GRID_CELLS.reduce<Record<OrbDefenseCell, number>>(
  (counts, cell) => {
    counts[cell.value] += 1;
    return counts;
  },
  { 0: 0, 1: 0, 2: 0 },
);

const GAME_CELL_SIZE = 0.62;
const GAME_WORLD_WIDTH = ORB_DEFENSE_GRID_WIDTH * GAME_CELL_SIZE;
const GAME_WORLD_DEPTH = ORB_DEFENSE_GRID_HEIGHT * GAME_CELL_SIZE;
const GAME_FLY_SECONDS = 10.5;
const SPLASH_TOP_GRID_Y = 33;
const SPLASH_TOP_GRID_Z = -12;
const SPLASH_BOTTOM_GRID_Y = -31;
const SPLASH_BOTTOM_GRID_Z = -18;
const GAME_GRID_Y = -10.35;
const GAME_GRID_Z = -1.6;
const GAME_GRID_SCALE = 0.34;
const GAME_ZOOM_STEP = 0.14;
const GAME_KEYBOARD_PAN_STEP = GAME_CELL_SIZE * 4.5;
const GAME_CAMERA_MIN_PITCH = -1.48;
const GAME_CAMERA_MAX_PITCH = 1.48;
const GAME_CAMERA_FOCUS_LIMIT_X = GAME_WORLD_WIDTH * 0.5 - GAME_CELL_SIZE * 0.5;
const GAME_CAMERA_FOCUS_LIMIT_Z = GAME_WORLD_DEPTH * 0.5 - GAME_CELL_SIZE * 0.5;
const GAME_RESOURCE_Z_OFFSET = GAME_WORLD_DEPTH * 0.86;
const GAME_PROJECTILE_MIN_LIFE_FRAMES = 24;
const SPLASH_BALL_RADIUS = 12;
const SPLASH_BALL_DETAIL = 4;
const TILE_HEIGHT: Record<OrbDefenseCell, number> = { 0: 0.08, 1: 1.08, 2: 0.24 };
const MAX_VISIBLE_ENEMIES = 96;
const MAX_VISIBLE_PROJECTILES = 96;
const GALAGA_FLY_SECONDS = 3.2;
const MAX_GALAGA_ENEMIES = 30;
const MAX_GALAGA_SHOTS = 36;
const MAX_GALAGA_EXPLOSIONS = 18;
const GALAGA_STAR_COUNT = 86;

const BLOCK_TEXT: Record<string, string[]> = {
  "0": ["111", "101", "101", "101", "101", "101", "111"],
  "1": ["010", "110", "010", "010", "010", "010", "111"],
  "2": ["111", "001", "001", "111", "100", "100", "111"],
  "3": ["111", "001", "001", "111", "001", "001", "111"],
  "4": ["101", "101", "101", "111", "001", "001", "001"],
  "5": ["111", "100", "100", "111", "001", "001", "111"],
  "6": ["111", "100", "100", "111", "101", "101", "111"],
  "7": ["111", "001", "001", "010", "010", "100", "100"],
  "8": ["111", "101", "101", "111", "101", "101", "111"],
  "9": ["111", "101", "101", "111", "001", "001", "111"],
  ".": ["0", "0", "0", "0", "0", "0", "1"],
  A: ["01110", "10001", "10001", "11111", "10001", "10001", "10001"],
  B: ["11110", "10001", "10001", "11110", "10001", "10001", "11110"],
  C: ["11111", "10000", "10000", "10000", "10000", "10000", "11111"],
  D: ["11110", "10001", "10001", "10001", "10001", "10001", "11110"],
  E: ["11111", "10000", "10000", "11110", "10000", "10000", "11111"],
  F: ["11111", "10000", "10000", "11110", "10000", "10000", "10000"],
  G: ["11111", "10000", "10000", "10111", "10001", "10001", "11111"],
  H: ["10001", "10001", "10001", "11111", "10001", "10001", "10001"],
  I: ["111", "010", "010", "010", "010", "010", "111"],
  K: ["10001", "10010", "10100", "11000", "10100", "10010", "10001"],
  L: ["10000", "10000", "10000", "10000", "10000", "10000", "11111"],
  M: ["10001", "11011", "10101", "10101", "10001", "10001", "10001"],
  N: ["10001", "11001", "10101", "10011", "10001", "10001", "10001"],
  O: ["11111", "10001", "10001", "10001", "10001", "10001", "11111"],
  P: ["11110", "10001", "10001", "11110", "10000", "10000", "10000"],
  R: ["11110", "10001", "10001", "11110", "10100", "10010", "10001"],
  S: ["11111", "10000", "10000", "11111", "00001", "00001", "11111"],
  T: ["11111", "00100", "00100", "00100", "00100", "00100", "00100"],
  U: ["10001", "10001", "10001", "10001", "10001", "10001", "11111"],
  V: ["10001", "10001", "10001", "10001", "10001", "01010", "00100"],
  W: ["10001", "10001", "10001", "10101", "10101", "11011", "10001"],
  Y: ["10001", "01010", "00100", "00100", "00100", "00100", "00100"],
};

const modulate = (value: number, minValue: number, maxValue: number, outMin: number, outMax: number) => {
  const fraction = (value - minValue) / (maxValue - minValue);
  return outMin + fraction * (outMax - outMin);
};

const clamp = (value: number, minValue: number, maxValue: number) => Math.max(minValue, Math.min(maxValue, value));

const easeOutCubic = (value: number) => 1 - Math.pow(1 - clamp(value, 0, 1), 3);

const lerp = (from: number, to: number, progress: number) => from + (to - from) * progress;

const segmentProgress = (progress: number, start: number, end: number) =>
  clamp((progress - start) / Math.max(0.0001, end - start), 0, 1);

const smoothStep = (value: number) => {
  const progress = clamp(value, 0, 1);
  return progress * progress * (3 - 2 * progress);
};

function getWorldBuildTimeline(progress: number, gameActive: boolean): WorldBuildTimeline {
  if (!gameActive && progress <= 0) {
    return {
      phase: "splash",
      progress: 0,
      arrival: 0,
      planes: 0,
      floor: 0,
      walls: 0,
      orbs: 0,
      enemies: 0,
    };
  }

  const eased = easeOutCubic(progress);
  const phase =
    progress >= 1
      ? "live"
      : progress < 0.18
        ? "planes_flatten"
        : progress < 0.45
          ? "floor_rise"
          : progress < 0.66
            ? "walls_rise"
            : progress < 0.84
              ? "orbs_deploy"
              : "enemy_spawn";

  return {
    phase,
    progress: eased,
    arrival: easeOutCubic(segmentProgress(progress, 0, 0.2)),
    planes: smoothStep(segmentProgress(progress, 0, 0.18)),
    floor: smoothStep(segmentProgress(progress, 0.16, 0.48)),
    walls: smoothStep(segmentProgress(progress, 0.44, 0.68)),
    orbs: smoothStep(segmentProgress(progress, 0.64, 0.84)),
    enemies: smoothStep(segmentProgress(progress, 0.82, 1)),
  };
}

function getRuntimeWorldBuildTimeline(
  runtime: RuntimeState,
  reducedMotion: boolean,
  elapsed = runtime.manualElapsed ?? performance.now() / 1000,
) {
  const progress = reducedMotion
    ? runtime.gameActive
      ? 1
      : 0
    : runtime.gameActive
      ? clamp((elapsed - runtime.transitionStartedAt) / GAME_FLY_SECONDS, 0, 1)
      : 0;

  return getWorldBuildTimeline(progress, runtime.gameActive);
}

function rowRevealProgress(progress: number, cellY: number) {
  const bottomToTop = (ORB_DEFENSE_GRID_HEIGHT - 1 - cellY) / Math.max(1, ORB_DEFENSE_GRID_HEIGHT - 1);
  return smoothStep(segmentProgress(progress, bottomToTop * 0.58, bottomToTop * 0.58 + 0.42));
}

function applyCameraProjection(runtime: RuntimeState, projection: OrbDefenseProjection) {
  runtime.projection = projection;

  if (projection === "reset") {
    runtime.focusTargetX = 0;
    runtime.focusTargetZ = 0;
    runtime.yawTarget = 0;
    runtime.pitchTarget = 0;
    runtime.zoomTarget = 0;
    return;
  }

  const sidePitch = 0.18;
  runtime.zoomTarget = Math.max(runtime.zoomTarget, 0.72);

  if (projection === "top") {
    runtime.yawTarget = 0;
    runtime.pitchTarget = GAME_CAMERA_MAX_PITCH;
    runtime.zoomTarget = 1;
    return;
  }

  if (projection === "bottom") {
    runtime.yawTarget = Math.PI;
    runtime.pitchTarget = GAME_CAMERA_MIN_PITCH;
    runtime.zoomTarget = 1;
    return;
  }

  runtime.pitchTarget = sidePitch;
  if (projection === "front") {
    runtime.yawTarget = 0;
  } else if (projection === "back") {
    runtime.yawTarget = Math.PI;
  } else if (projection === "left") {
    runtime.yawTarget = -Math.PI / 2;
  } else if (projection === "right") {
    runtime.yawTarget = Math.PI / 2;
  }
}

const layeredNoise = (x: number, y: number, z: number, time: number) =>
  Math.sin(x * 0.032 + time * 1.3) * 0.45 +
  Math.sin(y * 0.021 - time * 0.8) * 0.35 +
  Math.sin((x + y + z) * 0.016 + time * 0.55) * 0.2;

function readTerrainDeviceProfile(reducedMotion = false): TerrainDeviceProfile {
  const nav = typeof navigator === "undefined" ? null : navigator;
  const win = typeof window === "undefined" ? null : window;

  return {
    reducedMotion,
    userAgent: nav?.userAgent ?? "",
    platform: nav?.platform ?? "",
    maxTouchPoints: nav?.maxTouchPoints ?? 0,
    innerWidth: win?.innerWidth ?? 1024,
    innerHeight: win?.innerHeight ?? 768,
    devicePixelRatio: win?.devicePixelRatio ?? 1,
  };
}

export function shouldUseStaticTerrainBackdrop(profile: Partial<TerrainDeviceProfile> = {}) {
  const resolved = {
    ...readTerrainDeviceProfile(),
    ...profile,
  };
  const isIOS =
    /iPad|iPhone|iPod/i.test(resolved.userAgent) ||
    (resolved.platform === "MacIntel" && resolved.maxTouchPoints > 1);
  const narrowHighDprTouch =
    resolved.maxTouchPoints > 0 &&
    Math.min(resolved.innerWidth, resolved.innerHeight) <= 430 &&
    resolved.devicePixelRatio >= 3;

  return resolved.reducedMotion || isIOS || narrowHighDprTouch;
}

function getTerrainPixelRatio() {
  const profile = readTerrainDeviceProfile();
  const touchCap = profile.maxTouchPoints > 0 ? 1.25 : 1.75;
  return Math.min(profile.devicePixelRatio || 1, touchCap);
}

const syntheticBands = (time: number): AudioBands => {
  const bass = 0.5 + Math.sin(time * 1.4) * 0.28 + Math.sin(time * 2.7) * 0.14;
  const mid = 0.48 + Math.sin(time * 1.8 + 0.6) * 0.22 + Math.sin(time * 4.1) * 0.1;
  const treble = 0.5 + Math.sin(time * 2.1 + 1.2) * 0.24 + Math.sin(time * 5.3) * 0.08;
  const energy = (bass + mid + treble) / 3;
  return {
    bass: clamp(bass, 0, 1),
    mid: clamp(mid, 0, 1),
    treble: clamp(treble, 0, 1),
    energy: clamp(energy, 0, 1),
    peak: clamp(Math.max(bass, mid, treble), 0, 1),
    impact: clamp(energy + Math.sin(time * 5.8) * 0.12, 0, 1),
    bassImpact: clamp(bass + Math.sin(time * 2.8) * 0.16, 0, 1),
  };
};

const gridPointToWorld = ({ x, y }: OrbDefenseGridPoint) => ({
  x: (x - (ORB_DEFENSE_GRID_WIDTH - 1) / 2) * GAME_CELL_SIZE,
  z: (y - (ORB_DEFENSE_GRID_HEIGHT - 1) / 2) * GAME_CELL_SIZE,
});

function clampCameraFocusX(value: number) {
  return clamp(value, -GAME_CAMERA_FOCUS_LIMIT_X, GAME_CAMERA_FOCUS_LIMIT_X);
}

function clampCameraFocusZ(value: number) {
  return clamp(value, -GAME_CAMERA_FOCUS_LIMIT_Z, GAME_CAMERA_FOCUS_LIMIT_Z);
}

function setCameraFocusToGridPoint(runtime: RuntimeState, point: OrbDefenseGridPoint) {
  const worldPoint = gridPointToWorld(point);
  runtime.focusTargetX = clampCameraFocusX(worldPoint.x);
  runtime.focusTargetZ = clampCameraFocusZ(worldPoint.z);
}

function createGameplayState(): GameplayState {
  return {
    level: 0,
    processedLevel: 0,
    frameAccumulator: 0,
    lastElapsed: null,
    waveTimerFrames: ORB_DEFENSE_WAVE_SECONDS * ORB_DEFENSE_FRAME_RATE,
    passiveEnergyFrames: 0,
    energy: ORB_DEFENSE_STARTING_ENERGY,
    score: 0,
    scoreBonus: ORB_DEFENSE_INITIAL_SCORE_BONUS,
    lastReleasedBossWave: 0,
    status: "playing",
    targets: ORB_TARGETS.map((point) => ({ point, destroyed: false, levelBonusGiven: 0 })),
    enemies: [],
    spawners: [],
    turrets: [],
    shots: [],
    rewardBursts: [],
    selectedTurretIndex: 0,
    nextEnemyId: 1,
    nextTurretId: 1,
    nextShotId: 1,
    nextRewardBurstId: 1,
    spawnCursor: 0,
    message: "Build defenses",
    lastPointer: null,
    hoverPreview: null,
  };
}

function createHudState(gameplay: GameplayState | null, worldPhase: WorldBuildPhase): OrbDefenseHudState {
  if (!gameplay) {
    return {
      status: worldPhase === "splash" ? "splash" : "building",
      wave: 0,
      processedWave: 0,
      energy: ORB_DEFENSE_STARTING_ENERGY,
      score: 0,
      scoreBonus: ORB_DEFENSE_INITIAL_SCORE_BONUS,
      timeRemaining: ORB_DEFENSE_WAVE_SECONDS,
      orbsAlive: ORB_TARGETS.length,
      totalOrbs: ORB_TARGETS.length,
      enemies: 0,
      spawners: 0,
      turretsPlaced: 0,
      selectedTurretIndex: 0,
      selectedTurretLabel: ORB_DEFENSE_TURRETS[0].label,
      selectedTurretCost: ORB_DEFENSE_TURRETS[0].cost,
      message: worldPhase === "splash" ? "Splash" : "Build defenses",
      turretOptions: buildHudTurrets(null),
    };
  }

  const selected = ORB_DEFENSE_TURRETS[gameplay.selectedTurretIndex] ?? ORB_DEFENSE_TURRETS[0];
  const orbsAlive = gameplay.targets.filter((target) => !target.destroyed).length;
  return {
    status: gameplay.status,
    wave: gameplay.level,
    processedWave: gameplay.processedLevel,
    energy: gameplay.energy,
    score: gameplay.score,
    scoreBonus: gameplay.scoreBonus,
    timeRemaining: gameplay.waveTimerFrames / ORB_DEFENSE_FRAME_RATE,
    orbsAlive,
    totalOrbs: gameplay.targets.length,
    enemies: gameplay.enemies.length,
    spawners: gameplay.spawners.length,
    turretsPlaced: gameplay.turrets.length,
    selectedTurretIndex: gameplay.selectedTurretIndex,
    selectedTurretLabel: selected.label,
    selectedTurretCost: selected.cost,
    message: gameplay.message,
    turretOptions: buildHudTurrets(gameplay),
  };
}

function buildHudTurrets(gameplay: GameplayState | null) {
  const energy = gameplay?.energy ?? ORB_DEFENSE_STARTING_ENERGY;
  const selectedTurretIndex = gameplay?.selectedTurretIndex ?? 0;
  return ORB_DEFENSE_PLACEABLE_TURRET_INDEXES.map((index) => {
    const turret = ORB_DEFENSE_TURRETS[index] ?? ORB_DEFENSE_TURRETS[0];
    return {
      index,
      label: turret.label,
      name: turret.name,
      levelLabel: turret.levelLabel,
      rank: turret.rank,
      family: turret.family,
      description: turret.description,
      power: turret.power,
      rangeLabel: turret.rangeLabel,
      sizeLabel: turret.sizeLabel,
      costLabel: turret.costLabel,
      efficiency: turret.efficiency,
      purchaseRequirement: turret.purchaseRequirement,
      member: turret.member,
      color: turret.color,
      cost: turret.cost,
      range: turret.range,
      damage: turret.damage,
      cooldown: turret.cooldown,
      affordable: energy >= turret.cost,
      selected: index === selectedTurretIndex,
    };
  });
}

function normalizePlaceableTurretIndex(index: number) {
  const rounded = Math.round(clamp(index, 0, ORB_DEFENSE_TURRETS.length - 1));
  return ORB_DEFENSE_PLACEABLE_TURRET_INDEXES.includes(rounded) ? rounded : ORB_DEFENSE_PLACEABLE_TURRET_INDEXES[0];
}

function getNextLiveTarget(gameplay: GameplayState) {
  return gameplay.targets.find((target) => !target.destroyed) ?? null;
}

function getOrbsAlive(gameplay: GameplayState) {
  return gameplay.targets.filter((target) => !target.destroyed).length;
}

function startPendingWave(gameplay: GameplayState) {
  if (gameplay.processedLevel >= gameplay.level || gameplay.status !== "playing") {
    return;
  }

  const config = createOrbDefenseWaveConfig(gameplay.level);
  gameplay.processedLevel = gameplay.level;

  if (config.bossPrize > 0) {
    gameplay.scoreBonus += config.bossPrize;
  }

  if (config.amount > 0) {
    gameplay.spawners.push({
      level: gameplay.level,
      config,
      remaining: config.amount,
      counter: 0,
    });
  }

  gameplay.message = config.boss ? `${config.label} +${formatNumberForState(config.bossPrize)}` : `${config.label} wave`;
}

function spawnEnemy(gameplay: GameplayState, spawner: GameplaySpawner) {
  const target = getNextLiveTarget(gameplay);
  if (!target) {
    gameplay.status = "lost";
    gameplay.message = "All orbs destroyed";
    return;
  }

  const spawnPoint = SPAWN_LOCATIONS[gameplay.spawnCursor % SPAWN_LOCATIONS.length];
  gameplay.spawnCursor += 1;
  const path = findOrbDefensePath(spawnPoint, target.point);
  if (path.length === 0) {
    return;
  }

  gameplay.enemies.push({
    id: gameplay.nextEnemyId,
    position: { ...spawnPoint },
    path,
    pathIndex: 0,
    health: spawner.config.health,
    startingHealth: spawner.config.health,
    speed: spawner.config.speed,
    spawnPoint,
    level: spawner.level,
    kind: spawner.config.kind,
    label: spawner.config.label,
    energyReward: spawner.config.energyReward,
    scoreReward: spawner.config.scoreReward,
    boss: spawner.config.boss,
  });
  gameplay.nextEnemyId += 1;
}

function advanceGameplay(gameplay: GameplayState, elapsed: number) {
  if (gameplay.status !== "playing") {
    gameplay.lastElapsed = elapsed;
    return;
  }

  if (gameplay.lastElapsed === null) {
    gameplay.lastElapsed = elapsed;
    startPendingWave(gameplay);
    return;
  }

  const deltaSeconds = clamp(elapsed - gameplay.lastElapsed, 0, 2);
  gameplay.lastElapsed = elapsed;
  gameplay.frameAccumulator += deltaSeconds * ORB_DEFENSE_FRAME_RATE;
  const frames = Math.min(240, Math.floor(gameplay.frameAccumulator));
  gameplay.frameAccumulator -= frames;

  for (let frame = 0; frame < frames; frame += 1) {
    advanceGameplayFrame(gameplay);
    if (gameplay.status !== "playing") {
      break;
    }
  }
}

function advanceGameplayFrame(gameplay: GameplayState) {
  advanceWaveTimer(gameplay);
  startPendingWave(gameplay);
  advancePassiveOrbEnergy(gameplay);
  advanceSpawners(gameplay);
  advanceTurrets(gameplay);
  advanceShots(gameplay);
  advanceEnemies(gameplay);
  advanceRewardBursts(gameplay);
  releaseClearedBossBonuses(gameplay);
  maybeFinishGame(gameplay);
}

function advanceWaveTimer(gameplay: GameplayState) {
  if (gameplay.status !== "playing" || gameplay.level >= 99) {
    return;
  }

  gameplay.waveTimerFrames -= 1;
  if (gameplay.waveTimerFrames <= 0) {
    startNextWave(gameplay, false);
  }
}

function advancePassiveOrbEnergy(gameplay: GameplayState) {
  const orbsAlive = getOrbsAlive(gameplay);
  if (orbsAlive <= 0) {
    return;
  }

  gameplay.passiveEnergyFrames += 1;
  const period = ORB_DEFENSE_PASSIVE_ENERGY_SECONDS * ORB_DEFENSE_FRAME_RATE;
  while (gameplay.passiveEnergyFrames >= period) {
    gameplay.passiveEnergyFrames -= period;
    const energy = orbsAlive * ORB_DEFENSE_PASSIVE_ENERGY_PER_ORB;
    gameplay.energy += energy;
    gameplay.message = `Orbs +${formatNumberForState(energy)} energy`;
  }
}

function advanceSpawners(gameplay: GameplayState) {
  gameplay.spawners = gameplay.spawners.filter((spawner) => {
    if (spawner.remaining <= 0) {
      return false;
    }

    if (spawner.counter >= spawner.config.interval) {
      spawner.counter = 0;
      spawner.remaining -= 1;
      spawnEnemy(gameplay, spawner);
      return spawner.remaining > 0;
    }

    spawner.counter += 1;
    return true;
  });
}

function advanceTurrets(gameplay: GameplayState) {
  gameplay.turrets.forEach((turret) => {
    const definition = ORB_DEFENSE_TURRETS[turret.definitionIndex] ?? ORB_DEFENSE_TURRETS[0];
    if (turret.cooldown > turret.timer) {
      turret.timer += 1;
    }

    const target = findTurretTarget(turret, gameplay.enemies);
    if (!target || turret.timer < turret.cooldown) {
      return;
    }

    target.health -= turret.damage;
    turret.timer = 0;
    gameplay.shots.push({
      id: gameplay.nextShotId,
      from: { x: turret.cx, y: turret.cy },
      to: { ...target.position },
      ageFrames: 0,
      lifeFrames: getTurretProjectileLifeFrames(definition),
      color: getTurretProjectileColor(definition),
    });
    gameplay.nextShotId += 1;
  });
}

function findTurretTarget(turret: GameplayTurret, enemies: GameplayEnemy[]): GameplayEnemy | null {
  let target: GameplayEnemy | null = null;
  let minDistance = turret.range;

  for (const enemy of enemies) {
    if (enemy.health <= 0) {
      continue;
    }
    const distance = Math.hypot(turret.cx - enemy.position.x, turret.cy - enemy.position.y);
    if (distance < minDistance) {
      minDistance = distance;
      target = enemy;
    }
  }

  return target;
}

function getTurretProjectileColor(definition: OrbDefenseTurretDefinition) {
  switch (definition.family) {
    case "fast-blast":
      return 0xd8faff;
    case "chain-shot":
      return 0xff58f3;
    case "big-shot":
      return 0xa1ff58;
    case "ice-shot":
      return 0xb8fbff;
    case "beam-blast":
      return 0xffb34d;
    case "electroshock":
      return 0xfff064;
    case "barrier":
      return 0x9fc4ff;
    default:
      return definition.color;
  }
}

function getTurretProjectileLifeFrames(definition: OrbDefenseTurretDefinition) {
  return Math.max(GAME_PROJECTILE_MIN_LIFE_FRAMES, Math.round((definition.speed || 1) * 12));
}

function advanceShots(gameplay: GameplayState) {
  gameplay.shots = gameplay.shots.filter((shot) => {
    shot.ageFrames += 1;
    return shot.ageFrames < shot.lifeFrames;
  });
}

function advanceEnemies(gameplay: GameplayState) {
  gameplay.enemies = gameplay.enemies.filter((enemy) => {
    if (enemy.health <= 0) {
      const reward = getOrbDefenseMonsterReward(enemy);
      gameplay.energy += reward.energy;
      gameplay.score += reward.score;
      gameplay.rewardBursts.push({
        id: gameplay.nextRewardBurstId,
        origin: { ...enemy.position },
        reward: Math.max(reward.energy, reward.score),
        ageFrames: 0,
        lifeFrames: 82,
        dispatched: false,
      });
      gameplay.nextRewardBurstId += 1;
      gameplay.message = `${enemy.label} +${formatNumberForState(reward.score)}`;
      return false;
    }

    const finalPath = enemy.path[enemy.path.length - 1];
    if (!finalPath) {
      return false;
    }

    const destination = gameplay.targets.find(
      (target) => !target.destroyed && target.point.x === finalPath.x && target.point.y === finalPath.y,
    );

    if (!destination || enemy.pathIndex >= enemy.path.length - 1) {
      gameplay.targets = gameplay.targets.map((target) =>
        target.point.x === finalPath.x && target.point.y === finalPath.y ? { ...target, destroyed: true } : target,
      );
      const nextTarget = getNextLiveTarget(gameplay);
      if (!nextTarget) {
        gameplay.status = "lost";
        gameplay.message = "All orbs destroyed";
        return false;
      }

      enemy.path = findOrbDefensePath(enemy.position, nextTarget.point);
      enemy.pathIndex = 0;
      return enemy.path.length > 0;
    }

    const target = enemy.path[enemy.pathIndex + 1];
    const directionX = target.x - enemy.position.x;
    const directionY = target.y - enemy.position.y;
    const length = Math.max(0.0001, Math.hypot(directionX, directionY));
    enemy.position.x += (directionX / length) * enemy.speed;
    enemy.position.y += (directionY / length) * enemy.speed;

    if (Math.hypot(enemy.position.x - target.x, enemy.position.y - target.y) < enemy.speed) {
      enemy.position = { ...target };
      enemy.pathIndex += 1;
    }

    return true;
  });
}

function advanceRewardBursts(gameplay: GameplayState) {
  gameplay.rewardBursts = gameplay.rewardBursts.filter((burst) => {
    burst.ageFrames += 1;
    return burst.ageFrames < burst.lifeFrames;
  });
}

function releaseClearedBossBonuses(gameplay: GameplayState) {
  const bossWave = [11, 22, 33, 44, 55, 66, 77, 88, 99].find(
    (wave) => wave > gameplay.lastReleasedBossWave && wave <= gameplay.processedLevel,
  );
  if (!bossWave) {
    return;
  }

  const bossStillActive =
    gameplay.spawners.some((spawner) => spawner.level === bossWave) ||
    gameplay.enemies.some((enemy) => enemy.level === bossWave);
  if (bossStillActive) {
    return;
  }

  const scoreBonus = Math.round(gameplay.scoreBonus);
  const energyBonus = getOrbDefenseScoreBonusEnergy(scoreBonus, getOrbsAlive(gameplay));
  if (scoreBonus > 0) {
    gameplay.score += scoreBonus;
  }
  if (energyBonus > 0) {
    gameplay.energy += energyBonus;
  }
  gameplay.scoreBonus = 0;
  gameplay.lastReleasedBossWave = bossWave;
  gameplay.message = `Boss bonus +${formatNumberForState(scoreBonus)}`;
}

function maybeFinishGame(gameplay: GameplayState) {
  if (gameplay.status !== "playing") {
    return;
  }

  if (
    gameplay.level >= 99 &&
    gameplay.processedLevel >= 99 &&
    gameplay.enemies.length === 0 &&
    gameplay.spawners.length === 0
  ) {
    gameplay.status = "won";
    gameplay.message = "Winner";
  }
}

function startNextWave(gameplay: GameplayState, skipped: boolean) {
  if (gameplay.status !== "playing" || gameplay.level >= 99) {
    return;
  }

  if (skipped) {
    const timeRemaining = Math.max(0, gameplay.waveTimerFrames / ORB_DEFENSE_FRAME_RATE);
    const scoreBonus = getOrbDefenseTimeSkipBonus(timeRemaining);
    if (scoreBonus > 0) {
      gameplay.scoreBonus += scoreBonus;
      const immediateEnergy = Math.floor(scoreBonus / 20);
      gameplay.energy += immediateEnergy;
    }
  }

  gameplay.level += 1;
  gameplay.waveTimerFrames = ORB_DEFENSE_WAVE_SECONDS * ORB_DEFENSE_FRAME_RATE;
  gameplay.message = skipped ? `Wave ${gameplay.level} skipped` : `Wave ${gameplay.level}`;
}

function advanceWaveEarly(gameplay: GameplayState) {
  startNextWave(gameplay, true);
}

function isTurretAreaAvailable(gameplay: GameplayState, x: number, y: number, definition: OrbDefenseTurretDefinition) {
  for (let offsetY = 0; offsetY < definition.h; offsetY += 1) {
    for (let offsetX = 0; offsetX < definition.w; offsetX += 1) {
      const cellX = x + offsetX;
      const cellY = y + offsetY;
      if (
        cellX < 0 ||
        cellX >= ORB_DEFENSE_GRID_WIDTH ||
        cellY < 0 ||
        cellY >= ORB_DEFENSE_GRID_HEIGHT ||
        getOrbDefenseCell(cellX, cellY) !== 2 ||
        gameplay.turrets.some((turret) => cellX >= turret.x && cellX < turret.x + turret.w && cellY >= turret.y && cellY < turret.y + turret.h)
      ) {
        return false;
      }
    }
  }

  return true;
}

function findTurretAt(gameplay: GameplayState, point: OrbDefenseGridPoint) {
  return (
    gameplay.turrets.find(
      (turret) => point.x >= turret.x && point.x < turret.x + turret.w && point.y >= turret.y && point.y < turret.y + turret.h,
    ) ?? null
  );
}

function createPlacementPreview(gameplay: GameplayState, point: OrbDefenseGridPoint): PlacementPreviewState {
  const existing = findTurretAt(gameplay, point);
  if (existing) {
    const definition = ORB_DEFENSE_TURRETS[existing.definitionIndex] ?? ORB_DEFENSE_TURRETS[0];
    const upgrade = definition.upgrades[0] ?? null;
    return {
      mode: upgrade ? "upgrade" : "blocked",
      valid: Boolean(upgrade && gameplay.energy >= upgrade.cost),
      point: { x: existing.x, y: existing.y },
      cx: existing.cx,
      cy: existing.cy,
      w: existing.w,
      h: existing.h,
      currentRange: existing.range,
      nextRange: upgrade?.range ?? null,
    };
  }

  const definition = ORB_DEFENSE_TURRETS[gameplay.selectedTurretIndex] ?? ORB_DEFENSE_TURRETS[0];
  const available = isTurretAreaAvailable(gameplay, point.x, point.y, definition);
  const affordable = gameplay.energy >= definition.cost;
  return {
    mode: available && affordable ? "place" : "blocked",
    valid: available && affordable,
    point: { ...point },
    cx: point.x + definition.w / 2,
    cy: point.y + definition.h / 2,
    w: definition.w,
    h: definition.h,
    currentRange: null,
    nextRange: definition.range,
  };
}

function setGameplayHoverPreview(gameplay: GameplayState, point: OrbDefenseGridPoint | null) {
  gameplay.lastPointer = point;
  gameplay.hoverPreview = point ? createPlacementPreview(gameplay, point) : null;
}

function refreshGameplayHoverPreview(gameplay: GameplayState) {
  if (!gameplay.hoverPreview) {
    return;
  }
  gameplay.hoverPreview = createPlacementPreview(gameplay, gameplay.hoverPreview.point);
}

function placeOrUpgradeTurret(gameplay: GameplayState, point: OrbDefenseGridPoint) {
  gameplay.lastPointer = point;
  const existing = findTurretAt(gameplay, point);
  if (existing) {
    return upgradeTurret(gameplay, existing);
  }

  const definition = ORB_DEFENSE_TURRETS[gameplay.selectedTurretIndex] ?? ORB_DEFENSE_TURRETS[0];
  if (!isTurretAreaAvailable(gameplay, point.x, point.y, definition)) {
    gameplay.message = "Blocked";
    return null;
  }
  if (gameplay.energy < definition.cost) {
    gameplay.message = `Need ${formatNumberForState(definition.cost)}`;
    return null;
  }

  gameplay.energy -= definition.cost;
  const turret: GameplayTurret = {
    id: gameplay.nextTurretId,
    definitionIndex: gameplay.selectedTurretIndex,
    x: point.x,
    y: point.y,
    w: definition.w,
    h: definition.h,
    cx: point.x + definition.w / 2,
    cy: point.y + definition.h / 2,
    level: definition.rank,
    range: definition.range,
    damage: definition.damage,
    cooldown: definition.cooldown,
    speed: definition.speed,
    timer: 0,
  };
  gameplay.nextTurretId += 1;
  gameplay.turrets.push(turret);
  gameplay.message = `${definition.label} placed`;
  return turret;
}

function upgradeTurret(gameplay: GameplayState, turret: GameplayTurret) {
  const definition = ORB_DEFENSE_TURRETS[turret.definitionIndex] ?? ORB_DEFENSE_TURRETS[0];
  const upgrade = definition.upgrades[0];
  if (!upgrade) {
    gameplay.message = "Max level";
    return turret;
  }
  if (gameplay.energy < upgrade.cost) {
    gameplay.message = `Need ${formatNumberForState(upgrade.cost)}`;
    return null;
  }

  gameplay.energy -= upgrade.cost;
  turret.definitionIndex = upgrade.definitionIndex;
  turret.level = upgrade.rank;
  turret.range = upgrade.range;
  turret.damage = upgrade.damage;
  turret.cooldown = upgrade.cooldown;
  turret.speed = upgrade.speed;
  gameplay.message = `${upgrade.label}`;
  return turret;
}

function formatNumberForState(value: number) {
  if (Math.abs(value) >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(1)}B`;
  }
  if (Math.abs(value) >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(value) >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }
  return String(Math.round(value));
}

function roughenGround(plane: PlaneState, distortion: number, time: number) {
  const positions = plane.mesh.geometry.attributes.position as THREE.BufferAttribute;

  for (let i = 0; i < positions.count; i += 1) {
    const index = i * 3;
    const x = plane.base[index];
    const y = plane.base[index + 1];
    const wave = layeredNoise(x, y, 0, time) * distortion * 7;
    positions.setXYZ(i, x, y, wave);
  }

  positions.needsUpdate = true;
  plane.mesh.geometry.computeVertexNormals();
}

function roundBall(ball: BallState, radius = SPLASH_BALL_RADIUS) {
  const positions = ball.mesh.geometry.attributes.position as THREE.BufferAttribute;

  for (let i = 0; i < positions.count; i += 1) {
    const index = i * 3;
    const x = ball.base[index];
    const y = ball.base[index + 1];
    const z = ball.base[index + 2];
    const inverseLength = 1 / Math.max(0.0001, Math.hypot(x, y, z));
    positions.setXYZ(i, x * inverseLength * radius, y * inverseLength * radius, z * inverseLength * radius);
  }

  positions.needsUpdate = true;
  ball.mesh.geometry.computeVertexNormals();
}

function roughenBall(ball: BallState, bands: AudioBands, volume: number, time: number) {
  const positions = ball.mesh.geometry.attributes.position as THREE.BufferAttribute;
  const normalizedVolume = clamp(volume, 0, 1);
  const lowVolumeProgress = clamp(normalizedVolume / 0.5, 0, 1);
  const highVolumeProgress = clamp((normalizedVolume - 0.5) / 0.5, 0, 1);
  const volumeRadius =
    normalizedVolume < 0.5 ? lerp(7.4, 11.4, lowVolumeProgress) : lerp(11.4, 31, highVolumeProgress);
  const responseScale =
    normalizedVolume < 0.5 ? lerp(0.22, 0.42, lowVolumeProgress) : lerp(0.42, 1, highVolumeProgress);
  const radius =
    volumeRadius +
    (bands.bass * 3.8 + bands.energy * 1.7 + bands.impact * 1.8 + bands.bassImpact * 6.4) * responseScale;
  const bassPush =
    (modulate(Math.pow(bands.bass, 0.8), 0, 2.4, 0, 10.5) + bands.impact * 3.4 + bands.bassImpact * 11.5) *
    responseScale;
  const treblePush = modulate(bands.treble, 0, 2.4, 0, 5) * responseScale;

  for (let i = 0; i < positions.count; i += 1) {
    const index = i * 3;
    const x = ball.base[index];
    const y = ball.base[index + 1];
    const z = ball.base[index + 2];
    const inverseLength = 1 / Math.max(0.0001, Math.hypot(x, y, z));
    const normalX = x * inverseLength;
    const normalY = y * inverseLength;
    const normalZ = z * inverseLength;
    const broad = layeredNoise(normalX * 38, normalY * 38, normalZ * 38, time * 0.72);
    const fold = Math.sin(normalX * 7 + time * 1.1) * Math.cos(normalY * 6 - time * 0.9);
    const crest = Math.pow(Math.max(0, broad * 0.5 + 0.5), 2);
    const displacement =
      bassPush +
      broad * treblePush * 7 +
      fold * 2.2 * bands.mid +
      crest * 3.4 * bands.energy +
      crest * 2.6 * bands.impact +
      crest * 6.8 * bands.bassImpact;
    const nextRadius = radius + displacement;
    positions.setXYZ(i, normalX * nextRadius, normalY * nextRadius, normalZ * nextRadius);
  }

  positions.needsUpdate = true;
  ball.mesh.geometry.computeVertexNormals();
}

type EnemyVisualKit = ReturnType<typeof createEnemyVisualKit>;

function createEnemyVisualKit() {
  const material = (color: number, emissive = color, intensity = 0.72) =>
    new THREE.MeshStandardMaterial({
      color,
      emissive,
      emissiveIntensity: intensity,
      metalness: 0.08,
      roughness: 0.34,
      transparent: true,
      opacity: 0.94,
    });

  return {
    geometries: {
      largeOrb: new THREE.SphereGeometry(0.58, 18, 14),
      orb: new THREE.SphereGeometry(0.36, 16, 12),
      smallOrb: new THREE.SphereGeometry(0.2, 12, 10),
      cone: new THREE.ConeGeometry(0.18, 0.46, 10),
      spike: new THREE.ConeGeometry(0.12, 0.42, 8),
      barrel: new THREE.CylinderGeometry(0.1, 0.13, 0.72, 10),
      block: new THREE.BoxGeometry(0.28, 0.22, 0.38),
      ring: new THREE.TorusGeometry(0.48, 0.045, 8, 26),
    },
    materials: {
      purpleShell: material(0x4c1c9c, 0x7b2cff, 0.92),
      magentaCore: material(0xff62e8, 0xff24de, 1.25),
      blueMinion: material(0x5d7dff, 0x1f8cff, 1.05),
      orangeFoot: material(0xffa13a, 0xff6b25, 1),
      pinkPincer: material(0xff6c9e, 0xff2f86, 1.05),
      greyArmor: material(0xb8b6c6, 0x776f8c, 0.45),
      whiteCore: material(0xf7f3ff, 0xc4baff, 0.72),
      greenBody: material(0x5ed33b, 0x3fff25, 1),
      cyanBody: material(0x28dcff, 0x00d9ff, 1),
      goldTrim: material(0xffd04f, 0xff9f24, 0.95),
      blackEye: material(0x080910, 0x001020, 0.2),
    },
  };
}

function addEnemyPart(
  group: THREE.Group,
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  position: [number, number, number],
  scale: [number, number, number] = [1, 1, 1],
  rotation: [number, number, number] = [0, 0, 0],
) {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.set(...position);
  mesh.scale.set(...scale);
  mesh.rotation.set(...rotation);
  group.add(mesh);
  return mesh;
}

function addOrbitingEnemyPart(
  group: THREE.Group,
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  center: [number, number, number],
  radius: number,
  phase: number,
  scale: [number, number, number] = [1, 1, 1],
) {
  const mesh = addEnemyPart(group, geometry, material, center, scale);
  mesh.userData.orbit = {
    centerX: center[0],
    centerY: center[1],
    centerZ: center[2],
    radius,
    phase,
    speed: 1.2 + radius * 0.9,
  };
  return mesh;
}

function createOrbDefenseEnemy(index: number, kit: EnemyVisualKit) {
  const group = new THREE.Group();
  const { geometries, materials } = kit;
  const variant = index % 6;
  group.userData.variantName = ["first-fight", "pincer", "breakout", "dominion", "gridlock", "orb-central"][variant];

  if (variant === 0) {
    addEnemyPart(group, geometries.largeOrb, materials.purpleShell, [0, 0.06, 0], [1.34, 1.08, 1.16]);
    addEnemyPart(group, geometries.orb, materials.magentaCore, [0, 0.18, 0.1], [1.25, 1, 0.72]);
    const minionPositions: [number, number, number][] = [
      [-0.62, -0.22, -0.36],
      [0, -0.32, -0.52],
      [0.62, -0.22, -0.36],
    ];
    minionPositions.forEach(([x, y, z]) => {
      addEnemyPart(group, geometries.orb, materials.blueMinion, [x, y, z], [0.72, 0.8, 0.72]);
      addEnemyPart(group, geometries.cone, materials.orangeFoot, [x, y - 0.34, z - 0.08], [0.85, 0.55, 0.85], [Math.PI, 0, 0]);
    });
  } else if (variant === 1) {
    addEnemyPart(group, geometries.largeOrb, materials.purpleShell, [0, 0.02, 0], [1.05, 1, 1.05]);
    addEnemyPart(group, geometries.orb, materials.magentaCore, [-0.28, 0.22, -0.16], [0.72, 0.72, 0.72]);
    addEnemyPart(group, geometries.orb, materials.magentaCore, [0.42, -0.1, 0.18], [0.68, 0.68, 0.68]);
    [-0.6, 0.62].forEach((x, claw) => {
      addEnemyPart(group, geometries.orb, materials.pinkPincer, [x, 0.46, -0.08], [0.44, 0.32, 0.38]);
      addEnemyPart(group, geometries.spike, materials.pinkPincer, [x + (claw ? 0.2 : -0.2), 0.44, -0.36], [1, 1, 1], [Math.PI / 2, 0, claw ? -0.42 : 0.42]);
    });
    addEnemyPart(group, geometries.orb, materials.blueMinion, [-0.72, -0.38, -0.38], [0.44, 0.56, 0.44]);
    addEnemyPart(group, geometries.orb, materials.blueMinion, [-0.36, -0.48, -0.44], [0.4, 0.5, 0.4]);
  } else if (variant === 2) {
    addEnemyPart(group, geometries.largeOrb, materials.greyArmor, [0, 0.02, 0], [1.02, 0.9, 1.04]);
    addEnemyPart(group, geometries.orb, materials.magentaCore, [-0.78, 0, 0], [0.78, 0.78, 0.78]);
    addEnemyPart(group, geometries.orb, materials.magentaCore, [0.78, 0, 0], [0.78, 0.78, 0.78]);
    addEnemyPart(group, geometries.spike, materials.goldTrim, [-0.38, 0.6, -0.08], [0.8, 0.9, 0.8], [0, 0, -0.9]);
    [-0.36, 0.36].forEach((x) =>
      addEnemyPart(group, geometries.cone, materials.orangeFoot, [x, -0.48, -0.2], [0.8, 0.8, 0.8], [Math.PI, 0, 0]),
    );
  } else if (variant === 3) {
    addEnemyPart(group, geometries.largeOrb, materials.whiteCore, [0, 0.1, 0], [0.94, 0.94, 0.94]);
    addEnemyPart(group, geometries.ring, materials.greyArmor, [0, 0.1, 0], [1.15, 1.15, 1.15], [Math.PI / 2, 0, 0]);
    [-0.72, 0.72].forEach((x) =>
      addEnemyPart(group, geometries.barrel, materials.greyArmor, [x, 0.1, 0], [0.9, 0.9, 0.9], [0, 0, Math.PI / 2]),
    );
    for (let step = 0; step < 5; step += 1) {
      addOrbitingEnemyPart(group, geometries.smallOrb, materials.magentaCore, [0, -0.16, 0], 0.72, (Math.PI * 2 * step) / 5);
    }
  } else if (variant === 4) {
    addEnemyPart(group, geometries.orb, materials.magentaCore, [0, 0.18, 0], [0.9, 1.05, 0.8]);
    addEnemyPart(group, geometries.spike, materials.goldTrim, [-0.28, 0.74, -0.02], [0.9, 0.9, 0.9], [0, 0, 0.5]);
    addEnemyPart(group, geometries.spike, materials.goldTrim, [0.28, 0.74, -0.02], [0.9, 0.9, 0.9], [0, 0, -0.5]);
    addEnemyPart(group, geometries.orb, materials.blackEye, [-0.18, 0.22, -0.34], [0.2, 0.2, 0.2]);
    addEnemyPart(group, geometries.orb, materials.blackEye, [0.18, 0.22, -0.34], [0.2, 0.2, 0.2]);
    addEnemyPart(group, geometries.barrel, materials.blueMinion, [0, -0.48, -0.02], [0.82, 0.82, 0.82]);
    for (let step = 0; step < 4; step += 1) {
      addOrbitingEnemyPart(group, geometries.smallOrb, materials.magentaCore, [0, 0.02, 0], 0.9, (Math.PI * 2 * step) / 4);
    }
  } else {
    addEnemyPart(group, geometries.largeOrb, materials.purpleShell, [0, 0, 0], [1.35, 1.1, 1.18]);
    addEnemyPart(group, geometries.orb, materials.magentaCore, [0, 0.16, -0.08], [1.02, 0.9, 0.9]);
    addEnemyPart(group, geometries.orb, materials.magentaCore, [-0.78, 0.1, -0.08], [0.56, 0.56, 0.56]);
    addEnemyPart(group, geometries.orb, materials.magentaCore, [0.76, -0.06, 0.08], [0.52, 0.52, 0.52]);
  }

  group.scale.setScalar(1.05);
  return group;
}

type BlockTextAlign = "center" | "left";

function populateBlockTextGroup(
  group: THREE.Group,
  text: string,
  material: THREE.Material,
  geometry: THREE.BufferGeometry,
  cellSize: number,
  align: BlockTextAlign,
) {
  group.clear();
  const letterGap = cellSize * 1.18;
  const textWidth = text.split("").reduce((width, letter) => {
    const pattern = BLOCK_TEXT[letter.toUpperCase()];
    return width + (pattern ? pattern[0].length * cellSize + letterGap : cellSize * 3);
  }, 0);
  const originX = align === "center" ? -Math.max(0, textWidth - letterGap) / 2 : 0;
  let cursorX = 0;

  text.split("").forEach((letter) => {
    const pattern = BLOCK_TEXT[letter.toUpperCase()];
    if (!pattern) {
      cursorX += cellSize * 3;
      return;
    }

    const letterWidth = pattern[0].length;
    pattern.forEach((row, rowIndex) => {
      row.split("").forEach((value, columnIndex) => {
        if (value !== "1") {
          return;
        }
        const block = new THREE.Mesh(geometry, material);
        block.position.set(originX + cursorX + columnIndex * cellSize, 0, (rowIndex - (pattern.length - 1) / 2) * cellSize);
        group.add(block);
      });
    });
    cursorX += letterWidth * cellSize + letterGap;
  });

  group.userData.text = text;
}

function createBlockTextGroup(text: string, material: THREE.Material, cellSize = 0.44, align: BlockTextAlign = "center") {
  const group = new THREE.Group();
  const geometry = new THREE.BoxGeometry(cellSize * 0.82, 0.16, cellSize * 0.82);
  group.userData.textGeometry = geometry;
  group.userData.textMaterial = material;
  group.userData.textCellSize = cellSize;
  group.userData.textAlign = align;
  populateBlockTextGroup(group, text, material, geometry, cellSize, align);
  return group;
}

function updateBlockTextGroup(group: THREE.Group, text: string) {
  if (group.userData.text === text) {
    return;
  }
  populateBlockTextGroup(
    group,
    text,
    group.userData.textMaterial as THREE.Material,
    group.userData.textGeometry as THREE.BufferGeometry,
    Number(group.userData.textCellSize) || 0.44,
    (group.userData.textAlign as BlockTextAlign | undefined) ?? "center",
  );
}

function formatResourceBlockValue(value: number) {
  const rounded = Math.round(value);
  const absolute = Math.abs(rounded);
  if (absolute < 100_000) {
    return String(rounded);
  }

  const suffixes = [
    { value: 1_000_000_000, suffix: "B" },
    { value: 1_000_000, suffix: "M" },
    { value: 1_000, suffix: "K" },
  ];
  const suffix = suffixes.find((entry) => absolute >= entry.value) ?? suffixes[suffixes.length - 1];
  const scaled = rounded / suffix.value;
  const digits = Math.abs(scaled) >= 10 ? scaled.toFixed(0) : scaled.toFixed(1);
  return `${digits.replace(/\.0$/, "")}${suffix.suffix}`;
}

function createResourceAnchor(kind: ResourceAnchorKind) {
  const isEnergy = kind === "energy";
  const color = isEnergy ? 0x4dff8a : 0xff44dd;
  const emissive = isEnergy ? 0x27c02a : 0xff24de;
  const labelMaterial = new THREE.MeshStandardMaterial({
    color: isEnergy ? 0x76ffb0 : 0xff78eb,
    emissive,
    emissiveIntensity: 0.9,
    metalness: 0.06,
    roughness: 0.32,
    transparent: true,
    opacity: 0.9,
  });
  const valueMaterial = new THREE.MeshStandardMaterial({
    color: isEnergy ? 0xd8ffe5 : 0xffd4f7,
    emissive,
    emissiveIntensity: 1.05,
    metalness: 0.04,
    roughness: 0.28,
    transparent: true,
    opacity: 0.92,
  });
  const shellMaterial = new THREE.MeshBasicMaterial({
    color: isEnergy ? 0x00e5ff : 0x00d9ff,
    transparent: true,
    opacity: 0.64,
    wireframe: true,
  });
  const coreMaterial = new THREE.MeshStandardMaterial({
    color,
    emissive,
    emissiveIntensity: 1.25,
    metalness: 0.12,
    roughness: 0.18,
    transparent: true,
    opacity: 0.88,
  });
  const shellGeometry = new THREE.IcosahedronGeometry(1.15, 3);
  const coreGeometry = new THREE.IcosahedronGeometry(0.94, 3);
  const group = new THREE.Group();
  const label = createBlockTextGroup(isEnergy ? "ENERGY" : "SCORE", labelMaterial, 0.42);
  const value = createBlockTextGroup("0", valueMaterial, 0.34, "left");
  const shell = new THREE.Mesh(shellGeometry, shellMaterial);
  const core = new THREE.Mesh(coreGeometry, coreMaterial);
  const valueX = 3.7;
  const orbX = 11.65;
  const z = isEnergy ? GAME_RESOURCE_Z_OFFSET : -GAME_RESOURCE_Z_OFFSET;

  label.position.set(-5.2, 0.18, 0);
  value.position.set(valueX, 0.18, 0);
  shell.position.set(orbX, 1.18, 0);
  core.position.copy(shell.position);
  core.scale.setScalar(0.18);
  group.add(label, value, shell, core);
  group.position.set(0, 0.18, z);
  group.visible = false;

  return {
    anchor: {
      kind,
      group,
      label,
      value,
      shell,
      core,
      target: new THREE.Vector3(orbX, 1.36, z),
      displayedValue: 0,
      valueText: "0",
    } satisfies ResourceAnchor,
    geometries: [
      shellGeometry,
      coreGeometry,
      label.userData.textGeometry as THREE.BufferGeometry,
      value.userData.textGeometry as THREE.BufferGeometry,
    ],
    materials: [labelMaterial, valueMaterial, shellMaterial, coreMaterial],
  };
}

function seededUnit(seed: number) {
  const value = Math.sin(seed * 12.9898) * 43758.5453;
  return value - Math.floor(value);
}

function seededSigned(seed: number, amplitude: number) {
  return (seededUnit(seed) * 2 - 1) * amplitude;
}

function cubicBezierPoint(
  start: THREE.Vector3,
  controlA: THREE.Vector3,
  controlB: THREE.Vector3,
  end: THREE.Vector3,
  progress: number,
) {
  const inverse = 1 - progress;
  return new THREE.Vector3(
    inverse ** 3 * start.x + 3 * inverse ** 2 * progress * controlA.x + 3 * inverse * progress ** 2 * controlB.x + progress ** 3 * end.x,
    inverse ** 3 * start.y + 3 * inverse ** 2 * progress * controlA.y + 3 * inverse * progress ** 2 * controlB.y + progress ** 3 * end.y,
    inverse ** 3 * start.z + 3 * inverse ** 2 * progress * controlA.z + 3 * inverse * progress ** 2 * controlB.z + progress ** 3 * end.z,
  );
}

function createGameScene() {
  const world = new THREE.Group();
  const dummy = new THREE.Object3D();
  const counts = { ...GRID_COUNTS };
  const tileInstances: Record<OrbDefenseCell, TileInstanceData[]> = { 0: [], 1: [], 2: [] };

  const pathGeometry = new THREE.BoxGeometry(GAME_CELL_SIZE * 0.92, 0.08, GAME_CELL_SIZE * 0.92);
  const wallGeometry = new THREE.BoxGeometry(GAME_CELL_SIZE * 0.9, 1.08, GAME_CELL_SIZE * 0.9);
  const buildGeometry = new THREE.BoxGeometry(GAME_CELL_SIZE * 0.9, 0.24, GAME_CELL_SIZE * 0.9);
  const floorGeometry = new THREE.PlaneGeometry(GAME_WORLD_WIDTH + 2, GAME_WORLD_DEPTH + 2);
  const orbGeometry = new THREE.IcosahedronGeometry(GAME_CELL_SIZE * 2.8, 4);
  const pedestalGeometry = new THREE.CylinderGeometry(GAME_CELL_SIZE * 2.1, GAME_CELL_SIZE * 1.55, 0.22, 24);
  const spawnGeometry = new THREE.TorusGeometry(GAME_CELL_SIZE * 0.62, 0.055, 8, 24);
  const projectileGeometry = new THREE.SphereGeometry(0.32, 12, 10);
  const placementFootprintGeometry = new THREE.BoxGeometry(1, 1, 1);
  const placementRangeGeometry = new THREE.SphereGeometry(1, 32, 18);
  const enemyVisualKit = createEnemyVisualKit();

  const floorMaterial = new THREE.MeshBasicMaterial({
    color: 0x010506,
    transparent: true,
    opacity: 0.3,
    side: THREE.DoubleSide,
  });
  const pathMaterial = new THREE.MeshStandardMaterial({
    color: 0x1c0e18,
    emissive: 0x330e26,
    emissiveIntensity: 0.35,
    metalness: 0.06,
    roughness: 0.72,
    transparent: true,
    opacity: 0.74,
  });
  const wallMaterial = new THREE.MeshStandardMaterial({
    color: 0xff2fd6,
    emissive: 0xff2fd6,
    emissiveIntensity: 0.46,
    metalness: 0.12,
    roughness: 0.42,
    transparent: true,
    opacity: 0.82,
  });
  const buildMaterial = new THREE.MeshStandardMaterial({
    color: 0x00d9ff,
    emissive: 0x00b8ff,
    emissiveIntensity: 0.5,
    metalness: 0.1,
    roughness: 0.36,
    transparent: true,
    opacity: 0.78,
  });
  const pedestalMaterial = new THREE.MeshStandardMaterial({
    color: 0x27c02a,
    emissive: 0x27c02a,
    emissiveIntensity: 0.52,
    roughness: 0.38,
    transparent: true,
    opacity: 0.82,
  });
  const orbMaterial = new THREE.MeshStandardMaterial({
    color: 0xff24de,
    emissive: 0xff24de,
    emissiveIntensity: 1.2,
    metalness: 0.16,
    roughness: 0.2,
    transparent: true,
    opacity: 0.9,
  });
  const orbShellMaterial = new THREE.MeshBasicMaterial({
    color: 0x00e5ff,
    transparent: true,
    opacity: 0.56,
    wireframe: true,
  });
  const spawnMaterial = new THREE.MeshBasicMaterial({
    color: 0xf3a832,
    transparent: true,
    opacity: 0.76,
  });
  const projectileMaterial = new THREE.MeshBasicMaterial({
    color: 0x00e5ff,
    transparent: true,
    opacity: 0.96,
    blending: THREE.AdditiveBlending,
    depthTest: false,
    depthWrite: false,
  });
  const placementFootprintMaterial = new THREE.MeshBasicMaterial({
    color: 0x00e5ff,
    transparent: true,
    opacity: 0.3,
    depthWrite: false,
  });
  const placementCurrentRangeMaterial = new THREE.MeshBasicMaterial({
    color: 0x51ff77,
    transparent: true,
    opacity: 0.15,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  const placementNextRangeMaterial = new THREE.MeshBasicMaterial({
    color: 0x00d9ff,
    transparent: true,
    opacity: 0.18,
    side: THREE.DoubleSide,
    depthWrite: false,
  });

  const floor = new THREE.Mesh(floorGeometry, floorMaterial);
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = -0.1;
  world.add(floor);

  const tileMeshes: Record<OrbDefenseCell, THREE.InstancedMesh> = {
    0: new THREE.InstancedMesh(pathGeometry, pathMaterial, counts[0]),
    1: new THREE.InstancedMesh(wallGeometry, wallMaterial, counts[1]),
    2: new THREE.InstancedMesh(buildGeometry, buildMaterial, counts[2]),
  };
  const indexes: Record<OrbDefenseCell, number> = { 0: 0, 1: 0, 2: 0 };

  GRID_CELLS.forEach((cell) => {
    const position = gridPointToWorld(cell);
    const y = cell.value === 1 ? 0.44 : cell.value === 2 ? 0.08 : -0.04;
    const index = indexes[cell.value];
    dummy.position.set(position.x, y, position.z);
    dummy.rotation.set(0, 0, 0);
    dummy.scale.set(1, 1, 1);
    dummy.updateMatrix();
    tileMeshes[cell.value].setMatrixAt(index, dummy.matrix);
    tileInstances[cell.value].push({
      cell: { x: cell.x, y: cell.y },
      position,
      finalY: y,
      height: TILE_HEIGHT[cell.value],
      index,
    });
    indexes[cell.value] += 1;
  });

  Object.values(tileMeshes).forEach((mesh) => {
    mesh.instanceMatrix.needsUpdate = true;
    world.add(mesh);
  });

  const orbMeshes: GameSceneState["orbMeshes"] = [];
  const orbShells: GameSceneState["orbShells"] = [];
  const pedestals: GameSceneState["pedestals"] = [];

  ORB_TARGETS.forEach((target, index) => {
    const position = gridPointToWorld(target);
    const pedestal = new THREE.Mesh(pedestalGeometry, pedestalMaterial);
    pedestal.position.set(position.x, 0.24, position.z);
    pedestals.push(pedestal);
    world.add(pedestal);

    const orb = new THREE.Mesh(orbGeometry, orbMaterial.clone());
    orb.position.set(position.x, 2.8, position.z);
    orb.userData.phase = index * 0.9;
    orbMeshes.push(orb);
    world.add(orb);

    const shell = new THREE.Mesh(orbGeometry.clone(), orbShellMaterial.clone());
    shell.position.copy(orb.position);
    shell.scale.setScalar(1.18);
    shell.userData.phase = index * 0.9;
    orbShells.push(shell);
    world.add(shell);
  });

  const spawnGates: GameSceneState["spawnGates"] = [];

  SPAWN_LOCATIONS.forEach((spawn, index) => {
    const position = gridPointToWorld(spawn);
    const gate = new THREE.Mesh(spawnGeometry, spawnMaterial.clone());
    gate.position.set(position.x, 0.5, position.z);
    gate.rotation.x = Math.PI / 2;
    gate.rotation.z = index * 0.45;
    spawnGates.push(gate);
    world.add(gate);
  });

  const enemyGroups: THREE.Group[] = [];

  for (let index = 0; index < MAX_VISIBLE_ENEMIES; index += 1) {
    const enemy = createOrbDefenseEnemy(index, enemyVisualKit);
    enemy.userData.baseScale = 1.14 + (index % 4) * 0.07;
    enemy.visible = false;
    enemyGroups.push(enemy);
    world.add(enemy);
  }

  const projectileMeshes: GameSceneState["projectileMeshes"] = [];

  for (let index = 0; index < MAX_VISIBLE_PROJECTILES; index += 1) {
    const projectile = new THREE.Mesh(projectileGeometry, projectileMaterial.clone());
    projectile.visible = false;
    projectile.renderOrder = 30;
    projectileMeshes.push(projectile);
    world.add(projectile);
  }

  const placementPreview = new THREE.Group();
  const placementFootprint = new THREE.Mesh(placementFootprintGeometry, placementFootprintMaterial);
  const placementRangeCurrent = new THREE.Mesh(placementRangeGeometry, placementCurrentRangeMaterial);
  const placementRangeNext = new THREE.Mesh(placementRangeGeometry.clone(), placementNextRangeMaterial);
  placementPreview.visible = false;
  placementFootprint.renderOrder = 20;
  placementRangeCurrent.renderOrder = 18;
  placementRangeNext.renderOrder = 17;
  placementPreview.add(placementRangeNext, placementRangeCurrent, placementFootprint);
  world.add(placementPreview);

  const energyAnchor = createResourceAnchor("energy");
  const scoreAnchor = createResourceAnchor("score");
  const resourceAnchors = {
    energy: energyAnchor.anchor,
    score: scoreAnchor.anchor,
  };
  world.add(resourceAnchors.energy.group, resourceAnchors.score.group);

  const rewardParticleGeometry = new THREE.SphereGeometry(0.16, 10, 10);
  const rewardMaterials = {
    energy: new THREE.MeshBasicMaterial({
      color: 0x61ff9b,
      transparent: true,
      opacity: 0.92,
    }),
    score: new THREE.MeshBasicMaterial({
      color: 0xff4fe5,
      transparent: true,
      opacity: 0.92,
    }),
  };

  world.position.set(0, -46, -72);
  world.scale.setScalar(0.32);

  return {
    world,
    tileMeshes,
    tileInstances,
    matrixObject: dummy,
    floorMaterial,
    pathMaterial,
    wallMaterial,
    buildMaterial,
    pedestalMaterial,
    spawnGates,
    pedestals,
    tileMaterials: [floorMaterial, pathMaterial, wallMaterial, buildMaterial, pedestalMaterial, spawnMaterial],
    orbMeshes,
    orbShells,
    enemyGroups,
    projectileMeshes,
    turretGroups: new Map(),
    placementPreview,
    placementFootprint,
    placementRangeCurrent,
    placementRangeNext,
    resourceAnchors,
    rewardParticleGeometry,
    rewardMaterials,
    rewardParticles: [],
    geometries: [
      pathGeometry,
      wallGeometry,
      buildGeometry,
      floorGeometry,
      orbGeometry,
      pedestalGeometry,
      spawnGeometry,
      projectileGeometry,
      placementFootprintGeometry,
      placementRangeGeometry,
      placementRangeNext.geometry,
      rewardParticleGeometry,
      ...energyAnchor.geometries,
      ...scoreAnchor.geometries,
      ...Object.values(enemyVisualKit.geometries),
      ...orbShells.map((shell) => shell.geometry),
    ],
    materials: [
      floorMaterial,
      pathMaterial,
      wallMaterial,
      buildMaterial,
      pedestalMaterial,
      orbMaterial,
      orbShellMaterial,
      spawnMaterial,
      projectileMaterial,
      placementFootprintMaterial,
      placementCurrentRangeMaterial,
      placementNextRangeMaterial,
      rewardMaterials.energy,
      rewardMaterials.score,
      ...energyAnchor.materials,
      ...scoreAnchor.materials,
      ...Object.values(enemyVisualKit.materials),
      ...projectileMeshes.map((projectile) => projectile.material),
      ...orbMeshes.map((orb) => orb.material),
      ...orbShells.map((shell) => shell.material),
      ...world.children
        .filter((child): child is THREE.Mesh<THREE.BufferGeometry, THREE.Material> => child instanceof THREE.Mesh)
        .map((mesh) => mesh.material),
    ],
    counts,
    instanceStateKey: "",
  };
}

function createGalagaScene() {
  const world = new THREE.Group();
  const enemyKit = createEnemyVisualKit();
  const playerBodyGeometry = new THREE.ConeGeometry(0.82, 1.8, 4);
  const playerWingGeometry = new THREE.BoxGeometry(1.5, 0.18, 0.42);
  const playerCoreGeometry = new THREE.IcosahedronGeometry(0.45, 3);
  const thrusterGeometry = new THREE.ConeGeometry(0.18, 0.92, 12);
  const shotGeometry = new THREE.SphereGeometry(0.16, 10, 10);
  const enemyShotGeometry = new THREE.SphereGeometry(0.18, 10, 10);
  const explosionGeometry = new THREE.SphereGeometry(0.72, 14, 12);
  const reticleGeometry = new THREE.TorusGeometry(0.64, 0.026, 8, 48);
  const reticleCoreGeometry = new THREE.TorusGeometry(0.2, 0.018, 8, 32);
  const starGeometry = new THREE.SphereGeometry(0.05, 6, 4);
  const bodyMaterial = new THREE.MeshStandardMaterial({
    color: 0x00d9ff,
    emissive: 0x00b8ff,
    emissiveIntensity: 1.25,
    metalness: 0.12,
    roughness: 0.25,
  });
  const wingMaterial = new THREE.MeshStandardMaterial({
    color: 0xff2fd6,
    emissive: 0xff24de,
    emissiveIntensity: 1.05,
    metalness: 0.12,
    roughness: 0.32,
  });
  const coreMaterial = new THREE.MeshStandardMaterial({
    color: 0xff8af0,
    emissive: 0xff24de,
    emissiveIntensity: 1.35,
    metalness: 0.08,
    roughness: 0.18,
  });
  const thrusterMaterial = new THREE.MeshBasicMaterial({
    color: 0xffa536,
    transparent: true,
    opacity: 0.72,
  });
  const shotMaterial = new THREE.MeshBasicMaterial({
    color: 0x77f6ff,
    transparent: true,
    opacity: 0.94,
  });
  const enemyShotMaterial = new THREE.MeshBasicMaterial({
    color: 0xffcc4d,
    transparent: true,
    opacity: 0.9,
  });
  const explosionMaterial = new THREE.MeshBasicMaterial({
    color: 0xff4fe5,
    transparent: true,
    opacity: 0.65,
    wireframe: true,
  });
  const reticleMaterial = new THREE.MeshBasicMaterial({
    color: 0x8bf7ff,
    transparent: true,
    opacity: 0.72,
  });
  const reticleCoreMaterial = reticleMaterial.clone();
  reticleCoreMaterial.color.setHex(0xff8af0);
  reticleCoreMaterial.opacity = 0.58;
  const titleMaterial = new THREE.MeshStandardMaterial({
    color: 0xff78eb,
    emissive: 0xff24de,
    emissiveIntensity: 1.1,
    metalness: 0.06,
    roughness: 0.28,
  });
  const scoreMaterial = new THREE.MeshStandardMaterial({
    color: 0xd8faff,
    emissive: 0x00d9ff,
    emissiveIntensity: 1,
    metalness: 0.04,
    roughness: 0.28,
  });
  const weaponMaterial = new THREE.MeshStandardMaterial({
    color: 0xffd04f,
    emissive: 0xff8a24,
    emissiveIntensity: 0.9,
    metalness: 0.04,
    roughness: 0.28,
  });
  const boostMaterial = new THREE.MeshStandardMaterial({
    color: 0x80ffb4,
    emissive: 0x35d86b,
    emissiveIntensity: 0.85,
    metalness: 0.04,
    roughness: 0.28,
  });

  const player = new THREE.Group();
  const body = new THREE.Mesh(playerBodyGeometry, bodyMaterial);
  body.position.set(0, 0.18, 0);
  const leftWing = new THREE.Mesh(playerWingGeometry, wingMaterial);
  leftWing.position.set(-0.72, -0.36, 0);
  leftWing.rotation.z = -0.34;
  const rightWing = new THREE.Mesh(playerWingGeometry, wingMaterial);
  rightWing.position.set(0.72, -0.36, 0);
  rightWing.rotation.z = 0.34;
  const playerCore = new THREE.Mesh(playerCoreGeometry, coreMaterial);
  playerCore.position.set(0, -0.08, -0.16);
  const thrusterMeshes: GalagaSceneState["thrusterMeshes"] = [-0.34, 0.34].map((x) => {
    const thruster = new THREE.Mesh(thrusterGeometry, thrusterMaterial.clone());
    thruster.position.set(x, -1.05, 0.04);
    thruster.rotation.z = Math.PI;
    player.add(thruster);
    return thruster;
  });
  player.add(leftWing, rightWing, body, playerCore);
  world.add(player);

  const reticle = new THREE.Group();
  const reticleRing = new THREE.Mesh(reticleGeometry, reticleMaterial);
  const reticleCore = new THREE.Mesh(reticleCoreGeometry, reticleCoreMaterial);
  reticle.add(reticleRing, reticleCore);
  reticle.visible = false;
  world.add(reticle);

  const starMeshes: GalagaSceneState["starMeshes"] = [];
  for (let index = 0; index < GALAGA_STAR_COUNT; index += 1) {
    const starMaterial = new THREE.MeshBasicMaterial({
      color: index % 5 === 0 ? 0xffd04f : 0xd8faff,
      transparent: true,
      opacity: 0.52,
    });
    const star = new THREE.Mesh(starGeometry, starMaterial);
    star.visible = false;
    starMeshes.push(star);
    world.add(star);
  }

  const enemyGroups: THREE.Group[] = [];
  for (let index = 0; index < MAX_GALAGA_ENEMIES; index += 1) {
    const enemy = createOrbDefenseEnemy(index + 1, enemyKit);
    enemy.scale.setScalar(0.72);
    enemy.visible = false;
    enemyGroups.push(enemy);
    world.add(enemy);
  }

  const shotMeshes: GalagaSceneState["shotMeshes"] = [];
  for (let index = 0; index < MAX_GALAGA_SHOTS; index += 1) {
    const shot = new THREE.Mesh(shotGeometry, shotMaterial.clone());
    shot.visible = false;
    shotMeshes.push(shot);
    world.add(shot);
  }

  const enemyShotMeshes: GalagaSceneState["enemyShotMeshes"] = [];
  for (let index = 0; index < MAX_GALAGA_SHOTS; index += 1) {
    const shot = new THREE.Mesh(enemyShotGeometry, enemyShotMaterial.clone());
    shot.visible = false;
    enemyShotMeshes.push(shot);
    world.add(shot);
  }

  const explosionMeshes: GalagaSceneState["explosionMeshes"] = [];
  for (let index = 0; index < MAX_GALAGA_EXPLOSIONS; index += 1) {
    const explosion = new THREE.Mesh(explosionGeometry, explosionMaterial.clone());
    explosion.visible = false;
    explosionMeshes.push(explosion);
    world.add(explosion);
  }

  const title = createBlockTextGroup("GALAGA", titleMaterial, 0.42);
  title.position.set(0, 12.8, -4.2);
  title.rotation.x = -0.16;
  world.add(title);

  const scoreValue = createBlockTextGroup("0", scoreMaterial, 0.34);
  scoreValue.position.set(0, 10.55, -4);
  scoreValue.rotation.x = -0.16;
  world.add(scoreValue);

  const weaponValue = createBlockTextGroup("BEAM", weaponMaterial, 0.24, "left");
  weaponValue.position.set(-12.9, 11.3, -4);
  weaponValue.rotation.x = -0.16;
  world.add(weaponValue);

  const boostValue = createBlockTextGroup("B 100", boostMaterial, 0.22, "left");
  boostValue.position.set(-12.9, 10.38, -4);
  boostValue.rotation.x = -0.16;
  world.add(boostValue);

  world.visible = false;

  return {
    world,
    player,
    playerCore,
    thrusterMeshes,
    reticle,
    starMeshes,
    enemyGroups,
    shotMeshes,
    enemyShotMeshes,
    explosionMeshes,
    title,
    scoreValue,
    weaponValue,
    boostValue,
    geometries: [
      playerBodyGeometry,
      playerWingGeometry,
      playerCoreGeometry,
      thrusterGeometry,
      shotGeometry,
      enemyShotGeometry,
      explosionGeometry,
      reticleGeometry,
      reticleCoreGeometry,
      starGeometry,
      title.userData.textGeometry as THREE.BufferGeometry,
      scoreValue.userData.textGeometry as THREE.BufferGeometry,
      weaponValue.userData.textGeometry as THREE.BufferGeometry,
      boostValue.userData.textGeometry as THREE.BufferGeometry,
      ...Object.values(enemyKit.geometries),
    ],
    materials: [
      bodyMaterial,
      wingMaterial,
      coreMaterial,
      thrusterMaterial,
      reticleMaterial,
      reticleCoreMaterial,
      titleMaterial,
      scoreMaterial,
      weaponMaterial,
      boostMaterial,
      ...Object.values(enemyKit.materials),
      ...thrusterMeshes.map((mesh) => mesh.material),
      ...starMeshes.map((mesh) => mesh.material),
      ...shotMeshes.map((mesh) => mesh.material),
      ...enemyShotMeshes.map((mesh) => mesh.material),
      ...explosionMeshes.map((mesh) => mesh.material),
      shotMaterial,
      enemyShotMaterial,
      explosionMaterial,
    ],
  } satisfies GalagaSceneState;
}

function updateGalagaScene(game: GalagaSceneState, state: GalagaState | null, progress: number, elapsed: number) {
  game.world.visible = progress > 0.02;
  const eased = easeOutCubic(progress);
  game.world.position.set(0, lerp(28, 0, eased), lerp(-40, -10, eased));
  game.world.rotation.x = lerp(-0.12, -0.025, eased);
  game.world.scale.setScalar(lerp(0.64, 1, eased));

  game.title.visible = progress > 0.35;
  game.scoreValue.visible = progress > 0.45;
  game.weaponValue.visible = progress > 0.58;
  game.boostValue.visible = progress > 0.58;

  const starSpeed = state?.player.boosting ? 23 : 11;
  game.starMeshes.forEach((star, index) => {
    star.visible = progress > 0.1;
    const depth = (elapsed * starSpeed + index * 2.47) % 38;
    const angle = index * 2.399963;
    const radius = 3.4 + (index % 13) * 1.18;
    const widen = 0.75 + depth / 42;
    star.position.set(Math.cos(angle) * radius * widen, Math.sin(angle * 1.37) * 9.4, -2.4 - depth);
    star.scale.setScalar((state?.player.boosting ? 1.35 : 1) * (0.72 + (index % 5) * 0.18));
    star.material.opacity = 0.28 + (depth / 38) * 0.5;
  });

  if (!state) {
    game.player.visible = false;
    game.reticle.visible = false;
    game.enemyGroups.forEach((enemy) => {
      enemy.visible = false;
    });
    game.shotMeshes.forEach((shot) => {
      shot.visible = false;
    });
    game.enemyShotMeshes.forEach((shot) => {
      shot.visible = false;
    });
    game.explosionMeshes.forEach((explosion) => {
      explosion.visible = false;
    });
    return;
  }

  game.player.visible = state.player.view !== "cockpit";
  game.player.position.set(state.player.x, state.player.y, state.player.z);
  game.player.rotation.z = state.player.roll + Math.sin(elapsed * 5 + state.player.x * 0.18) * 0.08;
  game.player.rotation.x = state.player.boosting ? -0.1 : 0;
  const invulnerablePulse = state.player.invulnerableFrames > 0 ? 0.82 + Math.sin(elapsed * 22) * 0.1 : 1;
  game.player.scale.setScalar(invulnerablePulse * (state.player.boosting ? 1.08 : 1));
  game.playerCore.scale.setScalar(1 + Math.sin(elapsed * 5) * 0.08);
  game.thrusterMeshes.forEach((thruster, index) => {
    const flame = state.player.boosting ? 1.8 + Math.sin(elapsed * 26 + index) * 0.24 : 0.82 + Math.sin(elapsed * 14 + index) * 0.12;
    thruster.scale.set(1, flame, 1);
    thruster.material.opacity = state.player.boosting ? 0.9 : 0.5;
  });

  game.reticle.visible = true;
  game.reticle.position.set(
    clamp(state.player.x * 1.02, -GALAGA_BOUNDS_X, GALAGA_BOUNDS_X),
    clamp(state.player.y + 4.2, -GALAGA_BOUNDS_Y + 1.6, GALAGA_BOUNDS_Y + 1.8),
    state.player.z - 5.8,
  );
  game.reticle.rotation.z = elapsed * (state.player.weapon === "ray" ? 1.15 : 0.72);
  game.reticle.scale.setScalar(
    (state.player.view === "cockpit" ? 1.12 : 1) *
      (state.player.weapon === "arc" ? 1.18 : state.player.weapon === "ray" ? 0.76 : 0.94),
  );

  updateBlockTextGroup(game.scoreValue, String(Math.round(state.score)));
  updateBlockTextGroup(game.weaponValue, state.player.weapon.toUpperCase());
  updateBlockTextGroup(game.boostValue, `B ${Math.round(state.player.boostMeter)}`);

  game.enemyGroups.forEach((enemyGroup, index) => {
    const enemy = state.enemies[index];
    enemyGroup.visible = Boolean(enemy);
    if (!enemy) {
      return;
    }
    enemyGroup.position.set(enemy.x, enemy.y, enemy.z);
    enemyGroup.rotation.x = enemy.diving ? -0.42 + Math.sin(elapsed * 7 + enemy.phase) * 0.16 : 0;
    enemyGroup.rotation.y = elapsed * (enemy.diving ? 1.8 : 0.9) + enemy.phase;
    enemyGroup.rotation.z = enemy.diving ? Math.sin(elapsed * 9 + enemy.phase) * 0.58 : Math.sin(elapsed * 3 + enemy.phase) * 0.12;
    const healthScale = 0.82 + (enemy.health / Math.max(1, enemy.maxHealth)) * 0.18;
    const kindScale = enemy.kind === "bomber" ? 0.88 : enemy.kind === "ace" ? 0.82 : 0.72;
    enemyGroup.scale.setScalar(kindScale * healthScale * (enemy.diving ? 1.16 : 1));
  });

  game.shotMeshes.forEach((mesh, index) => {
    const shot = state.shots[index];
    mesh.visible = Boolean(shot);
    if (!shot) {
      return;
    }
    mesh.position.set(shot.x, shot.y, shot.z);
    mesh.rotation.z = Math.atan2(-shot.velocityX, shot.velocityY);
    mesh.rotation.x = Math.atan2(shot.velocityZ, shot.velocityY);
    mesh.material.color.setHex(shot.weapon === "ray" ? 0xb8fbff : shot.weapon === "arc" ? 0xff8af0 : 0x77f6ff);
    mesh.material.opacity = shot.weapon === "ray" ? 0.98 : 0.9;
    mesh.scale.set(shot.weapon === "ray" ? 0.54 : 0.72, shot.weapon === "ray" ? 4.8 : 2.9, shot.weapon === "ray" ? 0.54 : 0.72);
  });

  game.enemyShotMeshes.forEach((mesh, index) => {
    const shot = state.enemyShots[index];
    mesh.visible = Boolean(shot);
    if (!shot) {
      return;
    }
    mesh.position.set(shot.x, shot.y, shot.z);
    mesh.rotation.z = Math.atan2(-shot.velocityX, Math.abs(shot.velocityY));
    mesh.rotation.x = Math.atan2(shot.velocityZ, Math.abs(shot.velocityY));
    mesh.scale.set(0.78, shot.radius > 0.3 ? 2.8 : 2.1, 0.78);
  });

  game.explosionMeshes.forEach((mesh, index) => {
    const explosion = state.explosions[index];
    mesh.visible = Boolean(explosion);
    if (!explosion) {
      return;
    }
    const progress = explosion.ageFrames / Math.max(1, explosion.lifeFrames);
    mesh.position.set(explosion.x, explosion.y, explosion.z);
    mesh.scale.setScalar(0.28 + progress * 2.4);
    mesh.material.opacity = Math.max(0, 0.72 * (1 - progress));
  });
}

type TurretVisualPalette = {
  base: number;
  trim: number;
  dome: number;
  barrel: number;
  glow: number;
};

function getTurretVisualPalette(index: number): TurretVisualPalette {
  const palettes: TurretVisualPalette[] = [
    { base: 0x14258f, trim: 0x6d7384, dome: 0xb3b5bd, barrel: 0x3a65ff, glow: 0x2f7cff },
    { base: 0x4b188f, trim: 0xffce42, dome: 0x8b37ff, barrel: 0x2f7cff, glow: 0xff2fd6 },
    { base: 0x122313, trim: 0x93ff52, dome: 0x54c33b, barrel: 0x193d18, glow: 0x45ff2e },
    { base: 0x135d8a, trim: 0xd2f9ff, dome: 0x18d8ff, barrel: 0x00f0ff, glow: 0x00d9ff },
    { base: 0x552110, trim: 0xffc04a, dome: 0xf06f20, barrel: 0xff7b22, glow: 0xff8a24 },
    { base: 0x3f1684, trim: 0xffd85f, dome: 0xc86cff, barrel: 0x6c3bff, glow: 0xff24de },
    { base: 0x113b8f, trim: 0x78d5ff, dome: 0x247cff, barrel: 0x0b1840, glow: 0x4da3ff },
  ];
  return palettes[index % palettes.length];
}

function getTurretVisualTier(definition: OrbDefenseTurretDefinition) {
  const order = ["fast-blast", "chain-shot", "big-shot", "ice-shot", "beam-blast", "electroshock", "barrier"];
  return Math.max(0, order.indexOf(definition.family));
}

function createTurretMaterial(color: number, emissive = color, intensity = 0.74) {
  return new THREE.MeshStandardMaterial({
    color,
    emissive,
    emissiveIntensity: intensity,
    metalness: 0.16,
    roughness: 0.36,
    transparent: true,
    opacity: 0.92,
  });
}

function addTurretMesh(
  group: THREE.Group,
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  position: [number, number, number],
  scale: [number, number, number] = [1, 1, 1],
  rotation: [number, number, number] = [0, 0, 0],
) {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.set(...position);
  mesh.scale.set(...scale);
  mesh.rotation.set(...rotation);
  group.add(mesh);
  return mesh;
}

function ensureTurretVisual(game: GameSceneState, turret: GameplayTurret) {
  const existing = game.turretGroups.get(turret.id);
  if (existing) {
    updateTurretVisual(existing, turret);
    return existing;
  }

  const definition = ORB_DEFENSE_TURRETS[turret.definitionIndex] ?? ORB_DEFENSE_TURRETS[0];
  const tier = getTurretVisualTier(definition);
  const palette = getTurretVisualPalette(tier);
  const baseMaterial = createTurretMaterial(palette.base, palette.glow, 0.46);
  const trimMaterial = createTurretMaterial(palette.trim, palette.trim, 0.84);
  const domeMaterial = createTurretMaterial(palette.dome, palette.glow, 0.9);
  const barrelMaterial = createTurretMaterial(palette.barrel, palette.glow, 1.08);
  const glowMaterial = new THREE.MeshBasicMaterial({
    color: palette.glow,
    transparent: true,
    opacity: 0.72,
    wireframe: true,
  });
  const darkMaterial = createTurretMaterial(0x07080c, palette.glow, 0.18);
  const footprintGeometry = new THREE.BoxGeometry(
    definition.w * GAME_CELL_SIZE * 0.84,
    0.18,
    definition.h * GAME_CELL_SIZE * 0.84,
  );
  const baseRadius = GAME_CELL_SIZE * Math.max(0.34, Math.min(definition.w, definition.h) * 0.2);
  const baseGeometry = new THREE.CylinderGeometry(baseRadius * 0.78, baseRadius, GAME_CELL_SIZE * 0.64, tier === 0 ? 8 : 12);
  const domeGeometry = new THREE.SphereGeometry(GAME_CELL_SIZE * 0.34, 18, 12);
  const orbGeometry = new THREE.IcosahedronGeometry(GAME_CELL_SIZE * 0.3, 2);
  const barrelGeometry = new THREE.CylinderGeometry(GAME_CELL_SIZE * 0.08, GAME_CELL_SIZE * 0.1, GAME_CELL_SIZE * 0.92, 12);
  const heavyBarrelGeometry = new THREE.CylinderGeometry(GAME_CELL_SIZE * 0.13, GAME_CELL_SIZE * 0.16, GAME_CELL_SIZE * 1.16, 14);
  const finGeometry = new THREE.BoxGeometry(GAME_CELL_SIZE * 0.14, GAME_CELL_SIZE * 0.48, GAME_CELL_SIZE * 0.12);
  const plateGeometry = new THREE.BoxGeometry(GAME_CELL_SIZE * 0.5, GAME_CELL_SIZE * 0.18, GAME_CELL_SIZE * 0.16);
  const ringGeometry = new THREE.TorusGeometry(GAME_CELL_SIZE * 0.37, GAME_CELL_SIZE * 0.035, 8, 28);
  const group = new THREE.Group();
  group.userData.definitionIndex = turret.definitionIndex;

  addTurretMesh(group, footprintGeometry, darkMaterial, [0, 0.26, 0]);
  addTurretMesh(group, baseGeometry, baseMaterial, [0, 0.68, 0]);

  if (tier === 0) {
    addTurretMesh(group, domeGeometry, domeMaterial, [0, 1.05, 0], [1.06, 0.92, 1.06]);
    [-0.24, 0, 0.24].forEach((x) =>
      addTurretMesh(group, finGeometry, barrelMaterial, [x, 0.52, GAME_CELL_SIZE * 0.34], [0.82, 1, 0.82], [0.12, 0, 0]),
    );
    addTurretMesh(group, barrelGeometry, barrelMaterial, [0, 1.02, GAME_CELL_SIZE * 0.5], [0.9, 0.9, 0.9], [Math.PI / 2, 0, 0]);
  } else if (tier === 1) {
    addTurretMesh(group, domeGeometry, domeMaterial, [0, 1.03, 0], [1.1, 1.08, 1.1]);
    const ring = addTurretMesh(group, ringGeometry, trimMaterial, [0, 1.02, 0], [1.08, 1.08, 1.08], [Math.PI / 2, 0, 0]);
    ring.userData.spin = 0.018;
    [-0.28, 0.28].forEach((x) =>
      addTurretMesh(group, barrelGeometry, barrelMaterial, [x, 1.0, GAME_CELL_SIZE * 0.5], [0.78, 0.78, 0.78], [Math.PI / 2, 0, 0]),
    );
    addTurretMesh(group, orbGeometry, trimMaterial, [0, 1.44, -0.1], [0.52, 0.52, 0.52]);
  } else if (tier === 2) {
    addTurretMesh(group, domeGeometry, domeMaterial, [0, 1.05, 0], [0.94, 1.08, 0.94]);
    addTurretMesh(group, ringGeometry, glowMaterial, [0, 0.9, 0], [0.86, 0.86, 0.86], [Math.PI / 2, 0, 0]);
    [-0.18, 0.18].forEach((x) =>
      addTurretMesh(group, barrelGeometry, barrelMaterial, [x, 1.08, GAME_CELL_SIZE * 0.48], [0.72, 0.72, 0.9], [Math.PI / 2, 0, 0]),
    );
    addTurretMesh(group, plateGeometry, trimMaterial, [0, 0.48, -GAME_CELL_SIZE * 0.35], [0.9, 0.9, 0.9]);
  } else if (tier === 3) {
    addTurretMesh(group, baseGeometry, domeMaterial, [0, 1.04, 0], [0.7, 1.08, 0.7]);
    addTurretMesh(group, barrelGeometry, barrelMaterial, [0, 1.46, 0], [1, 1, 1.24], [0, 0, 0]);
    for (let step = 0; step < 4; step += 1) {
      const angle = (Math.PI * 2 * step) / 4;
      addTurretMesh(
        group,
        finGeometry,
        glowMaterial,
        [Math.cos(angle) * GAME_CELL_SIZE * 0.32, 0.9, Math.sin(angle) * GAME_CELL_SIZE * 0.32],
        [0.72, 0.92, 0.72],
        [0, angle, 0],
      );
    }
  } else if (tier === 4) {
    addTurretMesh(group, domeGeometry, domeMaterial, [0, 1.02, 0], [1.18, 0.94, 1.18]);
    addTurretMesh(group, heavyBarrelGeometry, barrelMaterial, [0, 1.08, GAME_CELL_SIZE * 0.58], [1, 1, 1], [Math.PI / 2, 0, 0]);
    [-0.28, 0.28].forEach((x) =>
      addTurretMesh(group, plateGeometry, trimMaterial, [x, 0.62, GAME_CELL_SIZE * 0.28], [0.7, 0.7, 0.7], [0, x > 0 ? -0.32 : 0.32, 0]),
    );
  } else if (tier === 5) {
    addTurretMesh(group, domeGeometry, domeMaterial, [0, 1.0, 0], [0.96, 1.02, 0.96]);
    const core = addTurretMesh(group, orbGeometry, glowMaterial, [0, 1.36, 0], [0.88, 0.88, 0.88]);
    core.userData.spin = 0.026;
    const halo = addTurretMesh(group, ringGeometry, trimMaterial, [0, 1.34, 0], [1.28, 1.28, 1.28], [Math.PI / 2, 0, 0]);
    halo.userData.spin = -0.018;
    addTurretMesh(group, barrelGeometry, barrelMaterial, [0, 1.2, GAME_CELL_SIZE * 0.58], [0.9, 0.9, 1.12], [Math.PI / 2, 0, 0]);
    [-0.32, 0.32].forEach((x) => addTurretMesh(group, orbGeometry, trimMaterial, [x, 0.58, -0.1], [0.36, 0.36, 0.36]));
  } else {
    addTurretMesh(group, plateGeometry, baseMaterial, [0, 0.9, 0], [1.22, 1.8, 0.46], [0, 0, 0]);
    addTurretMesh(group, plateGeometry, trimMaterial, [0, 1.16, GAME_CELL_SIZE * 0.18], [1.02, 1.25, 0.32], [0, 0, 0]);
    const halo = addTurretMesh(group, ringGeometry, glowMaterial, [0, 1.16, 0], [1.12, 1.12, 1.12], [Math.PI / 2, 0, 0]);
    halo.userData.spin = 0.012;
    addTurretMesh(group, orbGeometry, domeMaterial, [0, 1.62, 0], [0.5, 0.5, 0.5]);
  }

  game.world.add(group);
  game.turretGroups.set(turret.id, group);
  game.geometries.push(
    footprintGeometry,
    baseGeometry,
    domeGeometry,
    orbGeometry,
    barrelGeometry,
    heavyBarrelGeometry,
    finGeometry,
    plateGeometry,
    ringGeometry,
  );
  game.materials.push(baseMaterial, trimMaterial, domeMaterial, barrelMaterial, glowMaterial, darkMaterial);
  updateTurretVisual(group, turret);
  return group;
}

function updateTurretVisual(group: THREE.Group, turret: GameplayTurret) {
  const position = gridPointToWorld({ x: turret.cx - 0.5, y: turret.cy - 0.5 });
  group.position.set(position.x, 0, position.z);
  group.rotation.y += 0.018 + turret.level * 0.002;
  group.scale.setScalar(1 + (turret.level - 1) * 0.12);
  group.children.forEach((child) => {
    const spin = Number(child.userData.spin ?? 0);
    if (spin) {
      child.rotation.y += spin;
    }
  });
}

function syncTurretVisuals(game: GameSceneState, gameplay: GameplayState | null) {
  if (!gameplay) {
    game.turretGroups.forEach((group) => {
      group.visible = false;
    });
    return;
  }

  const activeIds = new Set(gameplay.turrets.map((turret) => turret.id));
  gameplay.turrets.forEach((turret) => {
    const group = ensureTurretVisual(game, turret);
    group.visible = true;
  });
  game.turretGroups.forEach((group, id) => {
    if (!activeIds.has(id)) {
      group.visible = false;
    }
  });
}

function updatePlacementPreview(game: GameSceneState, gameplay: GameplayState | null, timeline: WorldBuildTimeline) {
  const preview = gameplay?.hoverPreview ?? null;
  if (!preview || timeline.phase !== "live") {
    game.placementPreview.visible = false;
    return;
  }

  const position = gridPointToWorld({ x: preview.cx - 0.5, y: preview.cy - 0.5 });
  const isUpgrade = preview.mode === "upgrade";
  const invalidUpgrade = isUpgrade && !preview.valid;
  const blocked = preview.mode === "blocked";
  const footprintColor = preview.valid ? (isUpgrade ? 0x51ff77 : 0x00e5ff) : invalidUpgrade ? 0xffb83d : 0xff426e;
  const nextColor = preview.valid ? (isUpgrade ? 0xff4fe5 : 0x00d9ff) : invalidUpgrade ? 0xffb83d : 0xff426e;

  game.placementPreview.visible = true;
  game.placementFootprint.position.set(position.x, 0.34, position.z);
  game.placementFootprint.scale.set(preview.w * GAME_CELL_SIZE * 0.92, blocked ? 0.1 : 0.16, preview.h * GAME_CELL_SIZE * 0.92);
  game.placementFootprint.material.color.setHex(footprintColor);
  game.placementFootprint.material.opacity = preview.valid ? 0.34 : 0.24;
  game.placementFootprint.visible = true;

  game.placementRangeCurrent.position.set(position.x, 1.02, position.z);
  game.placementRangeCurrent.scale.setScalar(Math.max(0.01, (preview.currentRange ?? 0) * GAME_CELL_SIZE));
  game.placementRangeCurrent.material.opacity = preview.currentRange === null ? 0 : 0.16;
  game.placementRangeCurrent.visible = preview.currentRange !== null;

  game.placementRangeNext.position.set(position.x, 1.02, position.z);
  game.placementRangeNext.scale.setScalar(Math.max(0.01, (preview.nextRange ?? 0) * GAME_CELL_SIZE));
  game.placementRangeNext.material.color.setHex(nextColor);
  game.placementRangeNext.material.opacity = preview.nextRange === null ? 0 : preview.valid ? 0.18 : 0.11;
  game.placementRangeNext.visible = preview.nextRange !== null;
}

function updateResourceAnchors(game: GameSceneState, gameplay: GameplayState | null, timeline: WorldBuildTimeline, elapsed: number) {
  const values: Record<ResourceAnchorKind, number> = {
    energy: gameplay?.energy ?? 0,
    score: gameplay?.score ?? 0,
  };
  const caps: Record<ResourceAnchorKind, number> = {
    energy: 50_000,
    score: 250_000,
  };

  (Object.keys(game.resourceAnchors) as ResourceAnchorKind[]).forEach((kind, index) => {
    const anchor = game.resourceAnchors[kind];
    const deploy = easeOutCubic(segmentProgress(timeline.orbs, 0.18 + index * 0.08, 0.82 + index * 0.08));
    anchor.group.visible = deploy > 0.02;
    anchor.group.scale.setScalar(lerp(0.42, 1, deploy));
    anchor.group.position.y = lerp(-1.2, 0.18, deploy);
    anchor.displayedValue = lerp(anchor.displayedValue, values[kind], 0.12);
    const nextValueText = formatResourceBlockValue(values[kind]);
    if (anchor.valueText !== nextValueText) {
      anchor.valueText = nextValueText;
      updateBlockTextGroup(anchor.value, nextValueText);
    }
    const growth = clamp(Math.sqrt(Math.max(0, anchor.displayedValue) / caps[kind]), 0, 1);
    anchor.core.scale.setScalar(0.18 + growth * 0.96);
    anchor.core.rotation.x = elapsed * 0.82 + index;
    anchor.core.rotation.y = elapsed * 1.1;
    anchor.shell.rotation.x = -elapsed * 0.4;
    anchor.shell.rotation.y = elapsed * 0.58 + index;
  });
}

function spawnWorldRewardBursts(game: GameSceneState, gameplay: GameplayState | null, elapsed: number) {
  if (!gameplay) {
    return;
  }

  gameplay.rewardBursts.forEach((burst) => {
    if (burst.dispatched) {
      return;
    }

    const origin = gridPointToWorld(burst.origin);
    const startBase = new THREE.Vector3(origin.x, 2.65, origin.z);
    (["energy", "score"] as ResourceAnchorKind[]).forEach((kind) => {
      const anchor = game.resourceAnchors[kind];
      const count = kind === "energy" ? 7 : 9;
      for (let index = 0; index < count; index += 1) {
        const seed = burst.id * 157 + index * 41 + (kind === "score" ? 911 : 0);
        const start = startBase
          .clone()
          .add(new THREE.Vector3(seededSigned(seed + 1, 0.38), seededSigned(seed + 2, 0.24), seededSigned(seed + 3, 0.38)));
        const end = anchor.target
          .clone()
          .add(new THREE.Vector3(seededSigned(seed + 4, 0.54), seededSigned(seed + 5, 0.28), seededSigned(seed + 6, 0.54)));
        const controlA = start.clone().lerp(end, 0.32);
        const controlB = start.clone().lerp(end, 0.68);
        controlA.x += seededSigned(seed + 7, 8.5);
        controlA.y += 4.4 + seededUnit(seed + 8) * 7.2;
        controlA.z += seededSigned(seed + 9, 6.8);
        controlB.x += seededSigned(seed + 10, 8.2);
        controlB.y += 3.4 + seededUnit(seed + 11) * 6.4;
        controlB.z += seededSigned(seed + 12, 7.4);

        const mesh = new THREE.Mesh(game.rewardParticleGeometry, game.rewardMaterials[kind]);
        mesh.visible = false;
        game.world.add(mesh);
        game.rewardParticles.push({
          id: burst.id * 100 + index + (kind === "score" ? 50 : 0),
          kind,
          mesh,
          start,
          controlA,
          controlB,
          end,
          bornAt: elapsed,
          duration: 1.05 + seededUnit(seed + 13) * 0.38,
          delay: index * 0.035 + (kind === "score" ? 0.07 : 0),
        });
      }
    });
    burst.dispatched = true;
  });
}

function updateWorldRewardParticles(game: GameSceneState, elapsed: number) {
  game.rewardParticles = game.rewardParticles.filter((particle) => {
    const progress = (elapsed - particle.bornAt - particle.delay) / particle.duration;
    if (progress < 0) {
      particle.mesh.visible = false;
      return true;
    }
    if (progress >= 1) {
      game.world.remove(particle.mesh);
      return false;
    }

    const eased = smoothStep(progress);
    particle.mesh.visible = true;
    particle.mesh.position.copy(cubicBezierPoint(particle.start, particle.controlA, particle.controlB, particle.end, eased));
    particle.mesh.scale.setScalar(lerp(1.28, 0.28, progress) * (1 + Math.sin(progress * Math.PI) * 0.58));
    return true;
  });
}

function updateMaterialOpacity(material: THREE.Material, opacity: number) {
  material.transparent = true;
  material.opacity = opacity;
  material.needsUpdate = true;
}

function updateStandardMaterial(material: THREE.MeshStandardMaterial, opacity: number, emissiveIntensity: number) {
  updateMaterialOpacity(material, opacity);
  material.emissiveIntensity = emissiveIntensity;
}

function updateTileInstances(game: GameSceneState, floorProgress: number, wallProgress: number) {
  const floorKey = Math.round(floorProgress * 1000);
  const wallKey = Math.round(wallProgress * 1000);
  const stateKey = `${floorKey}:${wallKey}`;
  if (game.instanceStateKey === stateKey) {
    return;
  }

  game.instanceStateKey = stateKey;

  ([0, 2, 1] as OrbDefenseCell[]).forEach((cellType) => {
    const mesh = game.tileMeshes[cellType];
    const progress = cellType === 1 ? wallProgress : floorProgress;

    game.tileInstances[cellType].forEach((tile) => {
      const reveal = rowRevealProgress(progress, tile.cell.y);
      const blade = Math.sin(Math.PI * reveal) * 0.28;
      const scaleY = Math.max(0.001, reveal * (1 + blade));
      const baseBottom = tile.finalY - tile.height * 0.5;
      const centerY = baseBottom + tile.height * scaleY * 0.5;

      game.matrixObject.position.set(tile.position.x, centerY - (1 - reveal) * 0.18, tile.position.z);
      game.matrixObject.rotation.set(0, 0, 0);
      game.matrixObject.scale.set(1, scaleY, 1);
      game.matrixObject.updateMatrix();
      mesh.setMatrixAt(tile.index, game.matrixObject.matrix);
    });

    mesh.instanceMatrix.needsUpdate = true;
  });
}

function deployOrbProgress(progress: number, index: number) {
  const delay = index * 0.08;
  return easeOutCubic(segmentProgress(progress, delay, 0.62 + delay));
}

function updateGameScene(
  game: GameSceneState,
  timeline: WorldBuildTimeline,
  elapsed: number,
  bass: number,
  gameplay: GameplayState | null,
) {
  const eased = timeline.progress;
  const hover = Math.sin(elapsed * 0.8) * 0.35;
  const floorPulse = Math.sin(Math.PI * timeline.floor);
  const wallPulse = Math.sin(Math.PI * timeline.walls);
  const enemyPulse = Math.sin(Math.PI * timeline.enemies);

  game.world.visible = true;
  game.world.position.set(0, lerp(-46, -6.6 + hover * 0.22, timeline.arrival), lerp(-72, -1.6, timeline.arrival));
  game.world.rotation.set(
    lerp(-0.54, 0.02, timeline.arrival),
    lerp(0.5, Math.sin(elapsed * 0.08) * 0.04, timeline.arrival),
    0,
  );
  game.world.scale.setScalar(lerp(0.32, 1, timeline.arrival));

  updateTileInstances(game, timeline.floor, timeline.walls);

  updateMaterialOpacity(game.floorMaterial, lerp(0, 0.24, timeline.floor));
  updateStandardMaterial(game.pathMaterial, lerp(0, 0.74, timeline.floor), lerp(0.02, 0.35, timeline.floor) + floorPulse * 0.7);
  updateStandardMaterial(game.buildMaterial, lerp(0, 0.78, timeline.floor), lerp(0.04, 0.5, timeline.floor) + floorPulse * 1.05);
  updateStandardMaterial(game.wallMaterial, lerp(0, 0.82, timeline.walls), lerp(0.04, 0.46, timeline.walls) + wallPulse * 1.35);
  updateStandardMaterial(game.pedestalMaterial, lerp(0, 0.82, timeline.orbs), lerp(0.04, 0.52, timeline.orbs));

  game.orbMeshes.forEach((orb, index) => {
    const target = ORB_TARGETS[index];
    const targetPosition = gridPointToWorld(target);
    const deploy = deployOrbProgress(timeline.orbs, index);
    const destroyed = gameplay?.targets[index]?.destroyed ?? false;
    const pulse = 1 + Math.sin(elapsed * 2.8 + index) * 0.08 + bass * 0.1;
    const start = { x: 0, y: 8.4 + index * 0.08, z: 0 };
    const arc = Math.sin(Math.PI * deploy) * (4.2 + index * 0.18);
    orb.position.set(
      lerp(start.x, targetPosition.x, deploy),
      lerp(start.y, 2.8 + Math.sin(elapsed * 1.8 + Number(orb.userData.phase)) * 0.5, deploy) + arc,
      lerp(start.z, targetPosition.z, deploy),
    );
    orb.rotation.x = elapsed * 0.7 + index;
    orb.rotation.y = elapsed * 0.9;
    orb.scale.setScalar(lerp(0.34, destroyed ? 0.28 : pulse, deploy));
    updateMaterialOpacity(orb.material, lerp(0, destroyed ? 0.22 : 0.9, deploy));
  });

  game.orbShells.forEach((shell, index) => {
    const source = game.orbMeshes[index];
    const deploy = deployOrbProgress(timeline.orbs, index);
    const destroyed = gameplay?.targets[index]?.destroyed ?? false;
    shell.position.copy(source.position);
    shell.rotation.x = -elapsed * 0.42 + index;
    shell.rotation.y = elapsed * 0.54;
    shell.scale.setScalar((destroyed ? 0.62 : 1.18) + Math.sin(elapsed * 1.5 + index) * 0.05);
    updateMaterialOpacity(shell.material, lerp(0, destroyed ? 0.2 : 0.56, deploy));
  });

  game.pedestals.forEach((pedestal, index) => {
    const deploy = deployOrbProgress(timeline.orbs, index);
    pedestal.scale.set(1, Math.max(0.001, deploy), 1);
    pedestal.visible = deploy > 0.01;
  });

  game.spawnGates.forEach((gate, index) => {
    const gateProgress = easeOutCubic(segmentProgress(timeline.enemies, index * 0.035, 0.58 + index * 0.035));
    gate.visible = gateProgress > 0.01;
    gate.scale.setScalar(lerp(0.18, 1 + enemyPulse * 0.1, gateProgress));
    gate.rotation.z = index * 0.45 + elapsed * lerp(0.2, 1.8, gateProgress);
    updateMaterialOpacity(gate.material, lerp(0, 0.76, gateProgress));
  });

  syncTurretVisuals(game, gameplay);
  updatePlacementPreview(game, gameplay, timeline);
  updateResourceAnchors(game, gameplay, timeline, elapsed);
  spawnWorldRewardBursts(game, gameplay, elapsed);
  updateWorldRewardParticles(game, elapsed);

  game.enemyGroups.forEach((enemy, index) => {
    const data = gameplay?.enemies[index];
    if (!data || timeline.enemies < 0.2) {
      enemy.visible = false;
      return;
    }

    const position = gridPointToWorld(data.position);
    const healthRatio = clamp(data.health / Math.max(1, data.startingHealth), 0.16, 1);
    enemy.position.set(position.x, 2.18 + Math.sin(elapsed * 5 + index) * 0.32, position.z);
    enemy.rotation.y = elapsed * 1.8 + index * 0.4;
    enemy.rotation.z = Math.sin(elapsed * 3 + index) * 0.18;
    enemy.scale.setScalar((Number(enemy.userData.baseScale) || 1.2) * lerp(0.62, 1, healthRatio));
    enemy.children.forEach((child) => {
      const orbit = child.userData.orbit as
        | {
            centerX: number;
            centerY: number;
            centerZ: number;
            radius: number;
            phase: number;
            speed: number;
          }
        | undefined;
      if (!orbit) {
        return;
      }
      const angle = elapsed * orbit.speed + orbit.phase + index * 0.17;
      child.position.set(
        orbit.centerX + Math.cos(angle) * orbit.radius,
        orbit.centerY + Math.sin(elapsed * 2.2 + orbit.phase) * 0.08,
        orbit.centerZ + Math.sin(angle) * orbit.radius,
      );
    });
    enemy.visible = true;
  });

  game.projectileMeshes.forEach((projectile, index) => {
    const shot = gameplay?.shots[index];
    if (!shot || timeline.enemies < 0.2) {
      projectile.visible = false;
      return;
    }

    const from = gridPointToWorld(shot.from);
    const to = gridPointToWorld(shot.to);
    const progress = easeOutCubic(shot.ageFrames / Math.max(1, shot.lifeFrames));
    projectile.position.set(lerp(from.x, to.x, progress), 2.25 + Math.sin(Math.PI * progress) * 1.4, lerp(from.z, to.z, progress));
    projectile.lookAt(to.x, projectile.position.y, to.z);
    const boltPulse = 1 + Math.sin(progress * Math.PI * 8) * 0.1;
    const boltRadius = lerp(1.9, 0.72, progress) * boltPulse;
    projectile.scale.set(boltRadius, boltRadius, lerp(14, 3.2, progress));
    projectile.material.color.setHex(shot.color);
    projectile.material.opacity = lerp(1, 0.22, progress);
    projectile.visible = true;
  });
}

function disposeMaterial(material: THREE.Material | THREE.Material[]) {
  if (Array.isArray(material)) {
    material.forEach((entry) => entry.dispose());
    return;
  }
  material.dispose();
}

export function TechTerrain({ reducedMotion, gameActive, galagaActive }: TechTerrainProps) {
  const hostRef = useRef<HTMLSpanElement | null>(null);
  const audioRef = useRef<AudioNodes | null>(null);
  const audioBuffersRef = useRef<Map<number, AudioBuffer>>(new Map());
  const audioRequestRef = useRef(0);
  const volumeRef = useRef(defaultAudioPlayerState.volume);
  const renderRef = useRef<(() => void) | null>(null);
  const gameplayRef = useRef<GameplayState | null>(null);
  const galagaRef = useRef<GalagaState | null>(null);
  const galagaInputRef = useRef<GalagaInputState>(createGalagaInputState());
  const suppressAudioClickRef = useRef(false);
  const useStaticBackdrop = !gameActive && !galagaActive && shouldUseStaticTerrainBackdrop({ reducedMotion });
  const runtimeRef = useRef<RuntimeState>({
    gameActive,
    galagaActive,
    transitionStartedAt: performance.now() / 1000,
    galagaStartedAt: performance.now() / 1000,
    manualElapsed: null,
    zoom: 0,
    zoomTarget: 0,
    yaw: 0,
    yawTarget: 0,
    pitch: 0,
    pitchTarget: 0,
    focusX: 0,
    focusTargetX: 0,
    focusZ: 0,
    focusTargetZ: 0,
    projection: "reset",
    lastHudPublishAt: 0,
    snapshot: {},
  });
  const [audioState, setAudioState] = useState<AudioPlayerState>(defaultAudioPlayerState);
  const [webglMode, setWebglMode] = useState<WebglMode>(useStaticBackdrop ? "static" : "pending");

  const publishAudioState = useCallback((audio = audioRef.current) => {
    const trackIndex = audio?.trackIndex ?? defaultAudioPlayerState.trackIndex;
    const track = audioTracks[trackIndex] ?? audioTracks[0];
    const nextState: AudioPlayerState = {
      isPlaying: Boolean(audio?.isPlaying),
      trackIndex,
      trackTitle: track.title,
      trackArtist: track.artist,
      volume: audio?.master.gain.value ?? volumeRef.current,
    };
    setAudioState(nextState);
    window.dispatchEvent(new CustomEvent<AudioPlayerState>(AUDIO_STATE_EVENT, { detail: nextState }));
  }, []);

  const stopSource = useCallback((audio: AudioNodes) => {
    const source = audio.source;
    if (!source) {
      return;
    }

    source.onended = null;
    try {
      source.stop();
    } catch {
      // A source may already have ended naturally.
    }
    source.disconnect();
    audio.source = null;
  }, []);

  const loadTrackBuffer = useCallback(async (context: AudioContext, trackIndex: number) => {
    const cached = audioBuffersRef.current.get(trackIndex);
    if (cached) {
      return cached;
    }

    const track = audioTracks[trackIndex] ?? audioTracks[0];
    const response = await fetch(track.src);
    const audioData = await response.arrayBuffer();
    const buffer = await context.decodeAudioData(audioData);
    audioBuffersRef.current.set(trackIndex, buffer);
    return buffer;
  }, []);

  const ensureAudioNodes = useCallback(() => {
    const current = audioRef.current;
    if (current) {
      return current;
    }

    const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextConstructor) {
      return null;
    }

    const context = new AudioContextConstructor();
    const analyser = context.createAnalyser();
    const master = context.createGain();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.74;
    master.gain.value = volumeRef.current;
    analyser.connect(master);
    master.connect(context.destination);

    const audio: AudioNodes = {
      context,
      analyser,
      data: new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount)),
      waveform: new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount)),
      master,
      source: null,
      trackIndex: defaultAudioPlayerState.trackIndex,
      startedAt: 0,
      offset: 0,
      isPlaying: false,
      bands: {
        bass: 0.18,
        mid: 0.18,
        treble: 0.18,
        energy: 0.18,
        peak: 0.18,
        impact: 0.18,
        bassImpact: 0.18,
      },
    };
    audioRef.current = audio;
    publishAudioState(audio);
    return audio;
  }, [publishAudioState]);

  const stopAudio = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    audioRequestRef.current += 1;
    stopSource(audio);
    void audio.context.close();
    audioRef.current = null;
    publishAudioState(null);
  }, [publishAudioState, stopSource]);

  const playTrack = useCallback(
    async (trackIndex: number, offset = 0) => {
      const audio = ensureAudioNodes();
      if (!audio) {
        return;
      }

      const requestId = audioRequestRef.current + 1;
      audioRequestRef.current = requestId;
      const normalizedTrackIndex = (trackIndex + audioTracks.length) % audioTracks.length;
      stopSource(audio);
      audio.trackIndex = normalizedTrackIndex;
      audio.offset = offset;
      audio.isPlaying = true;
      publishAudioState(audio);

      const buffer = await loadTrackBuffer(audio.context, normalizedTrackIndex);
      if (audioRequestRef.current !== requestId || audioRef.current !== audio) {
        return;
      }

      if (audio.context.state === "suspended") {
        await audio.context.resume();
      }

      const source = audio.context.createBufferSource();
      const safeOffset = buffer.duration > 0 ? clamp(offset, 0, Math.max(0, buffer.duration - 0.05)) : 0;
      source.buffer = buffer;
      source.loop = false;
      source.connect(audio.analyser);
      source.onended = () => {
        if (audioRef.current?.source === source) {
          void playTrack(normalizedTrackIndex + 1, 0);
        }
      };

      audio.trackIndex = normalizedTrackIndex;
      audio.offset = safeOffset;
      audio.startedAt = audio.context.currentTime - safeOffset;
      audio.source = source;
      audio.isPlaying = true;
      source.start(0, safeOffset);
      publishAudioState(audio);
    },
    [ensureAudioNodes, loadTrackBuffer, publishAudioState, stopSource],
  );

  const pauseAudio = useCallback(() => {
    const audio = audioRef.current;
    if (!audio?.isPlaying) {
      return;
    }

    audioRequestRef.current += 1;
    const buffer = audioBuffersRef.current.get(audio.trackIndex);
    const elapsed = Math.max(0, audio.context.currentTime - audio.startedAt);
    audio.offset = buffer?.duration ? elapsed % buffer.duration : elapsed;
    audio.isPlaying = false;
    stopSource(audio);
    publishAudioState(audio);
  }, [publishAudioState, stopSource]);

  const togglePlayback = useCallback(() => {
    const audio = audioRef.current;
    if (audio?.isPlaying) {
      pauseAudio();
      return;
    }

    void playTrack(audio?.trackIndex ?? defaultAudioPlayerState.trackIndex, audio?.offset ?? 0);
  }, [pauseAudio, playTrack]);

  const nextTrack = useCallback(() => {
    const audio = audioRef.current;
    void playTrack((audio?.trackIndex ?? defaultAudioPlayerState.trackIndex) + 1, 0);
  }, [playTrack]);

  const previousTrack = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) {
      void playTrack(audioTracks.length - 1, 0);
      return;
    }

    const currentOffset = audio.isPlaying ? Math.max(0, audio.context.currentTime - audio.startedAt) : audio.offset;
    void playTrack(currentOffset > 3 ? audio.trackIndex : audio.trackIndex - 1, 0);
  }, [playTrack]);

  const setVolume = useCallback(
    (volume: number) => {
      const nextVolume = clamp(volume, 0, 1);
      volumeRef.current = nextVolume;
      if (audioRef.current) {
        audioRef.current.master.gain.value = nextVolume;
        publishAudioState(audioRef.current);
      } else {
        publishAudioState(null);
      }
      renderRef.current?.();
    },
    [publishAudioState],
  );

  useEffect(() => {
    const toggleAudio = () => {
      togglePlayback();
    };
    const handleAudioControl = (event: Event) => {
      const detail = (event as CustomEvent<AudioControlDetail>).detail;
      if (!detail) {
        return;
      }

      if (detail.action === "toggle") {
        togglePlayback();
        return;
      }
      if (detail.action === "previous") {
        previousTrack();
        return;
      }
      if (detail.action === "next") {
        nextTrack();
        return;
      }
      if (detail.action === "volume") {
        setVolume(detail.volume);
      }
    };

    window.addEventListener("miles-systems:toggle-background-audio", toggleAudio);
    window.addEventListener(AUDIO_CONTROL_EVENT, handleAudioControl);
    publishAudioState();
    return () => {
      window.removeEventListener("miles-systems:toggle-background-audio", toggleAudio);
      window.removeEventListener(AUDIO_CONTROL_EVENT, handleAudioControl);
    };
  }, [nextTrack, previousTrack, publishAudioState, setVolume, togglePlayback]);

  useEffect(() => {
    const now = performance.now() / 1000;
    const wasGameActive = runtimeRef.current.gameActive;
    const wasGalagaActive = runtimeRef.current.galagaActive;
    runtimeRef.current = {
      ...runtimeRef.current,
      gameActive,
      galagaActive,
      transitionStartedAt: gameActive && !wasGameActive ? now : runtimeRef.current.transitionStartedAt,
      galagaStartedAt: galagaActive && !wasGalagaActive ? now : runtimeRef.current.galagaStartedAt,
      zoom: gameActive ? runtimeRef.current.zoom : 0,
      zoomTarget: gameActive ? runtimeRef.current.zoomTarget : 0,
      yaw: gameActive ? runtimeRef.current.yaw : 0,
      yawTarget: gameActive ? runtimeRef.current.yawTarget : 0,
      pitch: gameActive ? runtimeRef.current.pitch : 0,
      pitchTarget: gameActive ? runtimeRef.current.pitchTarget : 0,
      focusX: gameActive ? runtimeRef.current.focusX : 0,
      focusTargetX: gameActive ? runtimeRef.current.focusTargetX : 0,
      focusZ: gameActive ? runtimeRef.current.focusZ : 0,
      focusTargetZ: gameActive ? runtimeRef.current.focusTargetZ : 0,
      projection: gameActive ? runtimeRef.current.projection : "reset",
    };
    gameplayRef.current = gameActive ? createGameplayState() : null;
    galagaRef.current = galagaActive ? createGalagaState() : null;
    galagaInputRef.current = createGalagaInputState();
    renderRef.current?.();
  }, [gameActive, galagaActive]);

  useEffect(() => {
    const handleGameControl = (event: Event) => {
      const detail = (event as CustomEvent<OrbDefenseControlDetail>).detail;
      if (!detail) {
        return;
      }

      if (detail.action === "camera-projection") {
        applyCameraProjection(runtimeRef.current, detail.projection);
        renderRef.current?.();
        return;
      }

      const gameplay = gameplayRef.current;
      if (!gameplay) {
        return;
      }

      if (detail.action === "select-turret") {
        gameplay.selectedTurretIndex = normalizePlaceableTurretIndex(detail.index);
        gameplay.message = `${ORB_DEFENSE_TURRETS[gameplay.selectedTurretIndex].label} selected`;
        refreshGameplayHoverPreview(gameplay);
        renderRef.current?.();
        return;
      }

      if (detail.action === "advance-wave") {
        const timeline = getRuntimeWorldBuildTimeline(runtimeRef.current, reducedMotion);
        if (timeline.phase !== "live") {
          gameplay.message = "Building";
          renderRef.current?.();
          return;
        }

        advanceWaveEarly(gameplay);
        refreshGameplayHoverPreview(gameplay);
        renderRef.current?.();
      }
    };

    window.addEventListener(ORB_DEFENSE_CONTROL_EVENT, handleGameControl);
    return () => window.removeEventListener(ORB_DEFENSE_CONTROL_EVENT, handleGameControl);
  }, [reducedMotion]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return undefined;
    }

    if (useStaticBackdrop) {
      setWebglMode("static");
      runtimeRef.current.snapshot = {
        mode: "splash",
        renderer: "static",
        audio: audioRef.current?.isPlaying ? "active" : "idle",
      };
      window.advanceTime = () => undefined;
      window.render_game_to_text = () => JSON.stringify(runtimeRef.current.snapshot);
      window.render_galaga_to_text = () => JSON.stringify(galagaRef.current ? serializeGalagaState(galagaRef.current) : null);
      renderRef.current = null;

      return () => {
        if (window.advanceTime) {
          delete window.advanceTime;
        }
        if (window.render_game_to_text) {
          delete window.render_game_to_text;
        }
        if (window.render_galaga_to_text) {
          delete window.render_galaga_to_text;
        }
      };
    }

    let frame = 0;
    let renderer: THREE.WebGLRenderer | null = null;
    let canvas: HTMLCanvasElement | null = null;
    let gameScene: GameSceneState | null = null;
    let galagaScene: GalagaSceneState | null = null;
    let ballIsRounded = true;
    let cleanup = () => undefined;

    const ensureGameScene = (scene: THREE.Scene) => {
      if (!gameScene) {
        gameScene = createGameScene();
        scene.add(gameScene.world);
      }
      return gameScene;
    };

    const ensureGalagaScene = (scene: THREE.Scene) => {
      if (!galagaScene) {
        galagaScene = createGalagaScene();
        scene.add(galagaScene.world);
      }
      return galagaScene;
    };

    try {
      setWebglMode("pending");
      const scene = new THREE.Scene();
      const visualizerGroup = new THREE.Group();
      const camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.1, 1000);
      camera.position.set(0, 2, 104);
      camera.lookAt(scene.position);
      scene.add(camera);
      scene.add(new THREE.AmbientLight(0xd8f4ff, 0.45));

      const keyLight = new THREE.DirectionalLight(0xffffff, 1.15);
      keyLight.position.set(-18, 34, 24);
      scene.add(keyLight);

      const magentaLight = new THREE.PointLight(0xff24de, 2.4, 140);
      magentaLight.position.set(0, 18, 18);
      scene.add(magentaLight);

      renderer = new THREE.WebGLRenderer({
        alpha: true,
        antialias: true,
        powerPreference: "high-performance",
        preserveDrawingBuffer: false,
      });
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.setClearColor(0x000000, 0);
      renderer.setPixelRatio(getTerrainPixelRatio());
      renderer.domElement.className = "tech-terrain-canvas";
      renderer.domElement.dataset.webgl = "ready";
      renderer.domElement.setAttribute("data-testid", "audio-three-canvas");
      renderer.domElement.setAttribute("aria-hidden", "true");
      canvas = renderer.domElement;
      host.appendChild(renderer.domElement);
      setWebglMode("ready");

      const planeGeometry = new THREE.PlaneGeometry(820, 820, 46, 46);
      const planeMaterial = new THREE.MeshBasicMaterial({
        color: 0x7f22ff,
        transparent: true,
        opacity: 0.5,
        side: THREE.DoubleSide,
        wireframe: true,
      });

      const topPlane = new THREE.Mesh(planeGeometry.clone(), planeMaterial);
      topPlane.rotation.x = -0.5 * Math.PI;
      topPlane.position.set(0, SPLASH_TOP_GRID_Y, SPLASH_TOP_GRID_Z);
      visualizerGroup.add(topPlane);

      const bottomPlane = new THREE.Mesh(planeGeometry.clone(), planeMaterial.clone());
      bottomPlane.material.color.setHex(0x00d9ff);
      bottomPlane.material.opacity = 0.42;
      bottomPlane.rotation.x = -0.5 * Math.PI;
      bottomPlane.position.set(0, SPLASH_BOTTOM_GRID_Y, SPLASH_BOTTOM_GRID_Z);
      visualizerGroup.add(bottomPlane);

      const ballGeometry = new THREE.IcosahedronGeometry(SPLASH_BALL_RADIUS, SPLASH_BALL_DETAIL);
      const ballMaterial = new THREE.MeshBasicMaterial({
        color: 0xff24de,
        transparent: true,
        opacity: 0.9,
        wireframe: true,
      });
      const ball = new THREE.Mesh(ballGeometry, ballMaterial);
      visualizerGroup.add(ball);

      const planeStates: PlaneState[] = [
        { mesh: topPlane, base: new Float32Array(topPlane.geometry.attributes.position.array), opacity: 0.5 },
        { mesh: bottomPlane, base: new Float32Array(bottomPlane.geometry.attributes.position.array), opacity: 0.42 },
      ];
      const ballState: BallState = {
        mesh: ball,
        base: new Float32Array(ball.geometry.attributes.position.array),
      };

      scene.add(visualizerGroup);

      const resize = () => {
        const width = Math.max(1, host.clientWidth || window.innerWidth);
        const height = Math.max(1, host.clientHeight || window.innerHeight);
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer?.setSize(width, height, false);
      };

      const currentElapsed = () => runtimeRef.current.manualElapsed ?? performance.now() / 1000;

      const transitionProgress = (elapsed: number) => {
        if (reducedMotion) {
          return runtimeRef.current.gameActive ? 1 : 0;
        }
        const raw = (elapsed - runtimeRef.current.transitionStartedAt) / GAME_FLY_SECONDS;
        return runtimeRef.current.gameActive ? clamp(raw, 0, 1) : 0;
      };

      const updateZoomTarget = (delta: number) => {
        const runtime = runtimeRef.current;
        if (!runtime.gameActive) {
          return;
        }
        runtime.zoomTarget = clamp(runtime.zoomTarget + delta, 0, 1);
        renderRef.current?.();
      };

      const updateCameraFocusTarget = (point: OrbDefenseGridPoint) => {
        setCameraFocusToGridPoint(runtimeRef.current, point);
        runtimeRef.current.projection = "free";
      };

      const panCameraFocusTarget = (deltaX: number, deltaZ: number, fast: boolean) => {
        const runtime = runtimeRef.current;
        const step = GAME_KEYBOARD_PAN_STEP * lerp(0.8, 2.2, runtime.zoomTarget) * (fast ? 1.9 : 1);
        runtime.focusTargetX = clampCameraFocusX(runtime.focusTargetX + deltaX * step);
        runtime.focusTargetZ = clampCameraFocusZ(runtime.focusTargetZ + deltaZ * step);
        runtime.projection = "free";
        renderRef.current?.();
      };

      const isEditableTarget = (target: EventTarget | null) => {
        if (!(target instanceof HTMLElement)) {
          return false;
        }
        return (
          target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement ||
          target instanceof HTMLSelectElement ||
          target.isContentEditable
        );
      };

      const raycaster = new THREE.Raycaster();
      const pointer = new THREE.Vector2();
      const localPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
      const localRay = new THREE.Ray();
      const localHit = new THREE.Vector3();
      let dragState:
        | {
            pointerId: number;
            mode: "orbit" | "pan";
            startX: number;
            startY: number;
            startYaw: number;
            startPitch: number;
            startFocusX: number;
            startFocusZ: number;
            moved: boolean;
          }
        | null = null;
      const touchPointers = new Map<number, { clientX: number; clientY: number }>();
      let touchGestureState:
        | {
            startDistance: number;
            startMidX: number;
            startMidY: number;
            startZoomTarget: number;
            startFocusX: number;
            startFocusZ: number;
            moved: boolean;
          }
        | null = null;

      const readTouchGesture = () => {
        const points = Array.from(touchPointers.values()).slice(0, 2);
        if (points.length < 2) {
          return null;
        }

        const [first, second] = points;
        const deltaX = second.clientX - first.clientX;
        const deltaY = second.clientY - first.clientY;
        return {
          distance: Math.max(1, Math.hypot(deltaX, deltaY)),
          midX: (first.clientX + second.clientX) / 2,
          midY: (first.clientY + second.clientY) / 2,
        };
      };

      const startTouchGesture = () => {
        const gesture = readTouchGesture();
        if (!gesture) {
          touchGestureState = null;
          return;
        }

        const midpoint = screenToGridPoint(gesture.midX, gesture.midY);
        if (midpoint) {
          updateCameraFocusTarget(midpoint);
        }

        touchGestureState = {
          startDistance: gesture.distance,
          startMidX: gesture.midX,
          startMidY: gesture.midY,
          startZoomTarget: runtimeRef.current.zoomTarget,
          startFocusX: runtimeRef.current.focusTargetX,
          startFocusZ: runtimeRef.current.focusTargetZ,
          moved: false,
        };
      };

      const updateTouchGesture = () => {
        const gesture = readTouchGesture();
        if (!gesture || !touchGestureState) {
          return false;
        }

        const midpointDeltaX = gesture.midX - touchGestureState.startMidX;
        const midpointDeltaY = gesture.midY - touchGestureState.startMidY;
        const pinchRatio = gesture.distance / touchGestureState.startDistance;
        const panScale = lerp(0.034, 0.16, runtimeRef.current.zoom);
        runtimeRef.current.zoomTarget = clamp(touchGestureState.startZoomTarget - Math.log(pinchRatio) * 0.42, 0, 1);
        runtimeRef.current.focusTargetX = clampCameraFocusX(touchGestureState.startFocusX - midpointDeltaX * panScale);
        runtimeRef.current.focusTargetZ = clampCameraFocusZ(touchGestureState.startFocusZ - midpointDeltaY * panScale);
        runtimeRef.current.projection = "free";
        if (Math.hypot(midpointDeltaX, midpointDeltaY) > 4 || Math.abs(gesture.distance - touchGestureState.startDistance) > 4) {
          touchGestureState.moved = true;
        }
        renderRef.current?.();
        return true;
      };

      const screenToGridPoint = (clientX: number, clientY: number) => {
        if (!canvas || !gameScene) {
          return null;
        }

        const rect = canvas.getBoundingClientRect();
        pointer.set(((clientX - rect.left) / Math.max(1, rect.width)) * 2 - 1, -(((clientY - rect.top) / Math.max(1, rect.height)) * 2 - 1));
        raycaster.setFromCamera(pointer, camera);
        gameScene.world.updateMatrixWorld(true);
        const inverseWorld = new THREE.Matrix4().copy(gameScene.world.matrixWorld).invert();
        localRay.copy(raycaster.ray).applyMatrix4(inverseWorld);

        if (!localRay.intersectPlane(localPlane, localHit)) {
          return null;
        }

        const x = Math.round(localHit.x / GAME_CELL_SIZE + (ORB_DEFENSE_GRID_WIDTH - 1) / 2);
        const y = Math.round(localHit.z / GAME_CELL_SIZE + (ORB_DEFENSE_GRID_HEIGHT - 1) / 2);
        if (x < 0 || x >= ORB_DEFENSE_GRID_WIDTH || y < 0 || y >= ORB_DEFENSE_GRID_HEIGHT) {
          return null;
        }

        return { x, y };
      };

      const updateHoverPreview = (clientX: number, clientY: number) => {
        const gameplay = gameplayRef.current;
        if (!runtimeRef.current.gameActive || !gameplay) {
          return;
        }

        const timeline = getWorldBuildTimeline(transitionProgress(currentElapsed()), runtimeRef.current.gameActive);
        const gridPoint = timeline.phase === "live" ? screenToGridPoint(clientX, clientY) : null;
        setGameplayHoverPreview(gameplay, gridPoint);
        renderRef.current?.();
      };

      const updateGalagaPointer = (clientX: number, clientY: number) => {
        const galaga = galagaRef.current;
        if (!canvas || !runtimeRef.current.galagaActive || !galaga) {
          return;
        }
        const rect = canvas.getBoundingClientRect();
        const normalizedX = ((clientX - rect.left) / Math.max(1, rect.width)) * 2 - 1;
        const normalizedY = ((clientY - rect.top) / Math.max(1, rect.height)) * 2 - 1;
        setGalagaPlayerFromPointer(galaga, normalizedX, normalizedY);
        renderRef.current?.();
      };

      const setGalagaInputForKey = (key: string, value: boolean) => {
        const normalized = key.toLowerCase();
        if (normalized === "arrowleft" || normalized === "a") {
          galagaInputRef.current.left = value;
          return true;
        }
        if (normalized === "arrowright" || normalized === "d") {
          galagaInputRef.current.right = value;
          return true;
        }
        if (normalized === "arrowup" || normalized === "w") {
          galagaInputRef.current.up = value;
          return true;
        }
        if (normalized === "arrowdown" || normalized === "s") {
          galagaInputRef.current.down = value;
          return true;
        }
        if (normalized === "e") {
          galagaInputRef.current.forward = value;
          return true;
        }
        if (normalized === "q") {
          galagaInputRef.current.back = value;
          return true;
        }
        if (normalized === "shift") {
          galagaInputRef.current.boost = value;
          return true;
        }
        return false;
      };

      const handleWheel = (event: WheelEvent) => {
        if (runtimeRef.current.galagaActive) {
          event.preventDefault();
          return;
        }
        if (!runtimeRef.current.gameActive) {
          return;
        }
        event.preventDefault();
        const rect = canvas?.getBoundingClientRect();
        const centeredPointer =
          rect &&
          Math.hypot(
            (event.clientX - (rect.left + rect.width / 2)) / Math.max(1, rect.width),
            (event.clientY - (rect.top + rect.height / 2)) / Math.max(1, rect.height),
          ) < 0.08;
        if (!centeredPointer) {
          const gridPoint = screenToGridPoint(event.clientX, event.clientY);
          if (gridPoint) {
            updateCameraFocusTarget(gridPoint);
          }
        }
        updateZoomTarget(event.deltaY / 900);
      };

      const handlePointerDown = (event: PointerEvent) => {
        if (runtimeRef.current.galagaActive) {
          event.preventDefault();
          updateGalagaPointer(event.clientX, event.clientY);
          if (event.button === 0 && galagaRef.current) {
            fireGalagaShot(galagaRef.current);
          }
          suppressAudioClickRef.current = true;
          renderRef.current?.();
          return;
        }

        if (!runtimeRef.current.gameActive) {
          return;
        }

        event.preventDefault();
        if (event.pointerType === "touch") {
          touchPointers.set(event.pointerId, { clientX: event.clientX, clientY: event.clientY });
          if (touchPointers.size >= 2) {
            dragState = null;
            startTouchGesture();
            suppressAudioClickRef.current = true;
            try {
              canvas?.setPointerCapture(event.pointerId);
            } catch {
              // Synthetic pointer events do not always create an active pointer capture target.
            }
            return;
          }
        }
        const mode = event.button === 2 || event.shiftKey || event.altKey ? "pan" : "orbit";
        dragState = {
          pointerId: event.pointerId,
          mode,
          startX: event.clientX,
          startY: event.clientY,
          startYaw: runtimeRef.current.yawTarget,
          startPitch: runtimeRef.current.pitchTarget,
          startFocusX: runtimeRef.current.focusTargetX,
          startFocusZ: runtimeRef.current.focusTargetZ,
          moved: false,
        };
        try {
          canvas?.setPointerCapture(event.pointerId);
        } catch {
          // Synthetic pointer events do not always create an active pointer capture target.
        }
      };

      const handlePointerMove = (event: PointerEvent) => {
        if (runtimeRef.current.galagaActive) {
          updateGalagaPointer(event.clientX, event.clientY);
          event.preventDefault();
          return;
        }

        if (!runtimeRef.current.gameActive) {
          return;
        }
        if (event.pointerType === "touch" && touchPointers.has(event.pointerId)) {
          touchPointers.set(event.pointerId, { clientX: event.clientX, clientY: event.clientY });
          if (touchPointers.size >= 2 && updateTouchGesture()) {
            event.preventDefault();
            return;
          }
        }
        if (!dragState || event.pointerId !== dragState.pointerId) {
          updateHoverPreview(event.clientX, event.clientY);
          return;
        }

        const deltaX = event.clientX - dragState.startX;
        const deltaY = event.clientY - dragState.startY;
        if (Math.hypot(deltaX, deltaY) > 4) {
          dragState.moved = true;
        }
        if (dragState.mode === "pan") {
          const panScale = lerp(0.034, 0.16, runtimeRef.current.zoom);
          runtimeRef.current.focusTargetX = clampCameraFocusX(dragState.startFocusX - deltaX * panScale);
          runtimeRef.current.focusTargetZ = clampCameraFocusZ(dragState.startFocusZ - deltaY * panScale);
        } else {
          runtimeRef.current.yawTarget = dragState.startYaw - deltaX * 0.006;
          runtimeRef.current.pitchTarget = clamp(
            dragState.startPitch + deltaY * 0.006,
            GAME_CAMERA_MIN_PITCH,
            GAME_CAMERA_MAX_PITCH,
          );
        }
        runtimeRef.current.projection = "free";
        event.preventDefault();
        renderRef.current?.();
      };

      const handlePointerUp = (event: PointerEvent) => {
        if (runtimeRef.current.galagaActive) {
          event.preventDefault();
          suppressAudioClickRef.current = true;
          return;
        }

        const finishedTouchGesture = event.pointerType === "touch" && touchGestureState?.moved;
        if (event.pointerType === "touch") {
          touchPointers.delete(event.pointerId);
          if (touchPointers.size < 2) {
            touchGestureState = null;
          }
        }
        if (finishedTouchGesture) {
          dragState = null;
          event.preventDefault();
          suppressAudioClickRef.current = true;
          return;
        }

        if (!dragState || event.pointerId !== dragState.pointerId) {
          return;
        }

        const wasDrag = dragState.moved;
        const wasCameraOnly = dragState.mode === "pan";
        dragState = null;
        try {
          canvas?.releasePointerCapture(event.pointerId);
        } catch {
          // The pointer can already be released by the browser or by synthetic test events.
        }

        if (!runtimeRef.current.gameActive) {
          return;
        }

        event.preventDefault();
        if (wasDrag || wasCameraOnly) {
          suppressAudioClickRef.current = true;
          return;
        }

        const timeline = getWorldBuildTimeline(transitionProgress(currentElapsed()), runtimeRef.current.gameActive);
        const gameplay = gameplayRef.current;
        const gridPoint = screenToGridPoint(event.clientX, event.clientY);
        if (timeline.phase === "live" && gameplay && gridPoint) {
          placeOrUpgradeTurret(gameplay, gridPoint);
          setGameplayHoverPreview(gameplay, gridPoint);
          suppressAudioClickRef.current = true;
          renderRef.current?.();
        }
      };

      const handlePointerCancel = (event: PointerEvent) => {
        if (event.pointerType === "touch") {
          touchPointers.delete(event.pointerId);
          if (touchPointers.size < 2) {
            touchGestureState = null;
          }
        }
        if (dragState?.pointerId === event.pointerId) {
          dragState = null;
        }
      };

      const handlePointerLeave = () => {
        touchPointers.clear();
        touchGestureState = null;
        const gameplay = gameplayRef.current;
        if (gameplay) {
          setGameplayHoverPreview(gameplay, null);
          renderRef.current?.();
        }
      };

      const handleContextMenu = (event: MouseEvent) => {
        if (runtimeRef.current.gameActive || runtimeRef.current.galagaActive) {
          event.preventDefault();
        }
      };

      const toggleFullscreen = () => {
        const target = host.parentElement ?? host;
        if (document.fullscreenElement) {
          void document.exitFullscreen();
          return;
        }
        void target.requestFullscreen?.();
      };

      const handleKeyDown = (event: KeyboardEvent) => {
        if (runtimeRef.current.galagaActive) {
          if (setGalagaInputForKey(event.key, true)) {
            event.preventDefault();
            renderRef.current?.();
            return;
          }
          if (event.code === "Space") {
            event.preventDefault();
            if (galagaRef.current) {
              fireGalagaShot(galagaRef.current);
              renderRef.current?.();
            }
            return;
          }
          if (event.key === "Tab" && !event.repeat) {
            event.preventDefault();
            if (galagaRef.current) {
              cycleGalagaWeapon(galagaRef.current);
              renderRef.current?.();
            }
            return;
          }
          if (event.key === "1" && !event.repeat) {
            event.preventDefault();
            if (galagaRef.current) {
              toggleGalagaView(galagaRef.current);
              renderRef.current?.();
            }
            return;
          }
          if (event.key.toLowerCase() === "r") {
            event.preventDefault();
            galagaRef.current = createGalagaState();
            galagaInputRef.current = createGalagaInputState();
            renderRef.current?.();
            return;
          }
          if (event.key.toLowerCase() === "f") {
            event.preventDefault();
            toggleFullscreen();
          }
          return;
        }

        if (!runtimeRef.current.gameActive) {
          return;
        }

        if (isEditableTarget(event.target)) {
          return;
        }

        if (event.key === "ArrowLeft") {
          event.preventDefault();
          panCameraFocusTarget(-1, 0, event.shiftKey);
          return;
        }

        if (event.key === "ArrowRight") {
          event.preventDefault();
          panCameraFocusTarget(1, 0, event.shiftKey);
          return;
        }

        if (event.key === "ArrowUp") {
          event.preventDefault();
          panCameraFocusTarget(0, -1, event.shiftKey);
          return;
        }

        if (event.key === "ArrowDown") {
          event.preventDefault();
          panCameraFocusTarget(0, 1, event.shiftKey);
          return;
        }

        if (event.key === "-" || event.key === "_") {
          event.preventDefault();
          updateZoomTarget(GAME_ZOOM_STEP);
          return;
        }

        if (event.key === "+" || event.key === "=") {
          event.preventDefault();
          updateZoomTarget(-GAME_ZOOM_STEP);
          return;
        }

        if (event.key === "0") {
          event.preventDefault();
          applyCameraProjection(runtimeRef.current, "reset");
          renderRef.current?.();
          return;
        }

        if (/^[1-7]$/.test(event.key)) {
          event.preventDefault();
          const gameplay = gameplayRef.current;
          if (gameplay) {
            gameplay.selectedTurretIndex =
              ORB_DEFENSE_PLACEABLE_TURRET_INDEXES[Number(event.key) - 1] ?? ORB_DEFENSE_PLACEABLE_TURRET_INDEXES[0];
            gameplay.message = `${ORB_DEFENSE_TURRETS[gameplay.selectedTurretIndex].label} selected`;
            refreshGameplayHoverPreview(gameplay);
            renderRef.current?.();
          }
          return;
        }

        if (event.code === "Space") {
          event.preventDefault();
          const gameplay = gameplayRef.current;
          if (gameplay) {
            const timeline = getWorldBuildTimeline(transitionProgress(currentElapsed()), runtimeRef.current.gameActive);
            if (timeline.phase !== "live") {
              gameplay.message = "Building";
              renderRef.current?.();
              return;
            }

            advanceWaveEarly(gameplay);
            renderRef.current?.();
          }
          return;
        }

        if (event.key.toLowerCase() === "f") {
          event.preventDefault();
          toggleFullscreen();
        }
      };

      const handleKeyUp = (event: KeyboardEvent) => {
        if (!runtimeRef.current.galagaActive) {
          return;
        }
        if (setGalagaInputForKey(event.key, false)) {
          event.preventDefault();
          renderRef.current?.();
        }
      };

      const render = () => {
        const elapsed = reducedMotion ? 6 : currentElapsed();
        const audio = audioRef.current;
        const isMusicPlaying = Boolean(audio?.isPlaying && audio.source);
        const bands = isMusicPlaying && audio ? readAudioBands(audio) : syntheticBands(elapsed);
        const progress = transitionProgress(elapsed);
        const timeline = getWorldBuildTimeline(progress, runtimeRef.current.gameActive);
        const galagaProgress = runtimeRef.current.galagaActive
          ? reducedMotion
            ? 1
            : clamp((elapsed - runtimeRef.current.galagaStartedAt) / GALAGA_FLY_SECONDS, 0, 1)
          : 0;
        if (runtimeRef.current.gameActive && !gameplayRef.current) {
          gameplayRef.current = createGameplayState();
        }
        if (runtimeRef.current.galagaActive && !galagaRef.current) {
          galagaRef.current = createGalagaState();
        }
        const gameplay = runtimeRef.current.gameActive ? gameplayRef.current : null;
        const galaga = runtimeRef.current.galagaActive ? galagaRef.current : null;
        if (gameplay && timeline.phase === "live") {
          advanceGameplay(gameplay, elapsed);
          refreshGameplayHoverPreview(gameplay);
        }
        if (galaga) {
          advanceGalagaElapsed(galaga, galagaInputRef.current, elapsed);
        }
        const waveScale = runtimeRef.current.gameActive
          ? lerp(1, 0, timeline.planes)
          : runtimeRef.current.galagaActive
            ? lerp(1, 0.24, galagaProgress)
            : 1;
        const zoomTarget = runtimeRef.current.gameActive ? runtimeRef.current.zoomTarget : 0;
        runtimeRef.current.zoomTarget = zoomTarget;
        runtimeRef.current.zoom = reducedMotion ? zoomTarget : lerp(runtimeRef.current.zoom, zoomTarget, 0.08);
        if (Math.abs(runtimeRef.current.zoom - zoomTarget) < 0.001) {
          runtimeRef.current.zoom = zoomTarget;
        }
        runtimeRef.current.yaw = reducedMotion
          ? runtimeRef.current.yawTarget
          : lerp(runtimeRef.current.yaw, runtimeRef.current.yawTarget, 0.12);
        runtimeRef.current.pitch = reducedMotion
          ? runtimeRef.current.pitchTarget
          : lerp(runtimeRef.current.pitch, runtimeRef.current.pitchTarget, 0.12);
        runtimeRef.current.focusX = reducedMotion
          ? runtimeRef.current.focusTargetX
          : lerp(runtimeRef.current.focusX, runtimeRef.current.focusTargetX, 0.12);
        runtimeRef.current.focusZ = reducedMotion
          ? runtimeRef.current.focusTargetZ
          : lerp(runtimeRef.current.focusZ, runtimeRef.current.focusTargetZ, 0.12);
        if (Math.abs(runtimeRef.current.focusX - runtimeRef.current.focusTargetX) < 0.01) {
          runtimeRef.current.focusX = runtimeRef.current.focusTargetX;
        }
        if (Math.abs(runtimeRef.current.focusZ - runtimeRef.current.focusTargetZ) < 0.01) {
          runtimeRef.current.focusZ = runtimeRef.current.focusTargetZ;
        }
        const overviewZoom = timeline.arrival * runtimeRef.current.zoom;
        const cameraOpticalZoom =
          runtimeRef.current.gameActive && !runtimeRef.current.galagaActive
            ? lerp(1, camera.aspect < 1 ? 0.56 : 0.46, overviewZoom)
            : 1;
        if (Math.abs(camera.zoom - cameraOpticalZoom) > 0.001) {
          camera.zoom = cameraOpticalZoom;
          camera.updateProjectionMatrix();
        }

        roughenGround(planeStates[0], modulate(bands.treble, 0, 1, 0.55, 4) * waveScale, elapsed);
        roughenGround(planeStates[1], modulate(bands.bass, 0, 1, 0.55, 4) * waveScale, elapsed * 0.9);
        if (isMusicPlaying) {
          roughenBall(ballState, bands, volumeRef.current, elapsed);
          ballIsRounded = false;
        } else if (!ballIsRounded) {
          roundBall(ballState);
          ballIsRounded = true;
        }

        const topTargetY = runtimeRef.current.galagaActive ? 15 : GAME_GRID_Y + 0.1;
        const topTargetZ = runtimeRef.current.galagaActive ? -26 : GAME_GRID_Z;
        const bottomTargetY = runtimeRef.current.galagaActive ? -15 : GAME_GRID_Y - 0.1;
        const bottomTargetZ = runtimeRef.current.galagaActive ? -26 : GAME_GRID_Z;
        const planeProgress = runtimeRef.current.galagaActive ? galagaProgress : timeline.planes;
        topPlane.position.set(
          0,
          lerp(SPLASH_TOP_GRID_Y, topTargetY, planeProgress),
          lerp(SPLASH_TOP_GRID_Z, topTargetZ, planeProgress),
        );
        bottomPlane.position.set(
          0,
          lerp(SPLASH_BOTTOM_GRID_Y, bottomTargetY, planeProgress),
          lerp(SPLASH_BOTTOM_GRID_Z, bottomTargetZ, planeProgress),
        );
        const gridScale = runtimeRef.current.galagaActive ? lerp(1, 0.22, galagaProgress) : lerp(1, GAME_GRID_SCALE, timeline.planes);
        topPlane.scale.setScalar(gridScale);
        bottomPlane.scale.setScalar(gridScale);

        planeStates.forEach((plane, index) => {
          const activePlaneProgress = runtimeRef.current.galagaActive ? galagaProgress : timeline.planes;
          const flatOpacity = lerp(1, index === 0 ? 0.38 : 0.46, activePlaneProgress);
          const floorFade = runtimeRef.current.galagaActive ? lerp(1, 0.72, galagaProgress) : lerp(1, 0.04, timeline.floor);
          plane.mesh.material.opacity = plane.opacity * flatOpacity * floorFade;
        });
        ball.material.opacity = runtimeRef.current.gameActive
          ? lerp(0.9, 0.08, timeline.orbs)
          : runtimeRef.current.galagaActive
            ? lerp(0.9, 0.04, galagaProgress)
            : 0.9;
        const volumeScaleLimit = clamp(volumeRef.current / 0.5, 0, 1);
        const audioScale = isMusicPlaying
          ? volumeRef.current < 0.5
            ? 0.78 + volumeScaleLimit * 0.18
            : 1 + bands.energy * 0.08 + bands.impact * 0.18 + bands.bassImpact * 0.42
          : 1;
        ball.scale.setScalar(
          runtimeRef.current.gameActive
            ? lerp(audioScale, 0.26, timeline.orbs)
            : runtimeRef.current.galagaActive
              ? lerp(audioScale, 0.18, galagaProgress)
              : audioScale,
        );

        visualizerGroup.rotation.y = runtimeRef.current.galagaActive
          ? lerp(elapsed * 0.18, elapsed * 0.04, galagaProgress)
          : lerp(elapsed * 0.18, 0, timeline.planes);
        visualizerGroup.rotation.z = runtimeRef.current.galagaActive
          ? lerp(Math.sin(elapsed * 0.35) * 0.045, 0, galagaProgress)
          : lerp(Math.sin(elapsed * 0.35) * 0.045, 0, timeline.planes);
        const activeGameScene = runtimeRef.current.gameActive || progress > 0 ? ensureGameScene(scene) : null;
        if (activeGameScene) {
          updateGameScene(activeGameScene, timeline, elapsed, bands.bass, timeline.phase === "live" ? gameplay : null);
        }
        const activeGalagaScene = runtimeRef.current.galagaActive || galagaProgress > 0 ? ensureGalagaScene(scene) : null;
        if (activeGalagaScene) {
          updateGalagaScene(activeGalagaScene, galaga, galagaProgress, elapsed);
        }

        const fov = THREE.MathUtils.degToRad(camera.fov);
        const fitDepth = Math.max(GAME_WORLD_DEPTH, GAME_RESOURCE_Z_OFFSET * 2 + 8);
        const fitDistance = Math.max(
          fitDepth / (2 * Math.tan(fov / 2)),
          GAME_WORLD_WIDTH / (2 * Math.tan(fov / 2) * Math.max(0.1, camera.aspect)),
        );
        const closeDistance = fitDistance * (camera.aspect < 1 ? 0.58 : 0.72);
        const gameplayDistance = closeDistance;
        const gameplayLookY = -5.1;
        const gameplayLookZ = 0;
        const focusX = lerp(0, runtimeRef.current.focusX, timeline.arrival);
        const focusY = lerp(0, gameplayLookY, timeline.arrival);
        const focusZ = lerp(0, gameplayLookZ + runtimeRef.current.focusZ, timeline.arrival);
        const activePitch = runtimeRef.current.pitch * timeline.arrival;
        const orbitDistance = gameplayDistance * 0.86;
        const horizontalRadius = Math.cos(activePitch) * orbitDistance;
        const verticalOffset = Math.sin(activePitch) * orbitDistance;
        const horizonLift = 14;
        const activeCameraX = focusX + Math.sin(runtimeRef.current.yaw) * horizontalRadius;
        const activeCameraY = focusY + horizonLift + verticalOffset;
        const activeCameraZ = focusZ + Math.cos(runtimeRef.current.yaw) * horizontalRadius;
        if (runtimeRef.current.galagaActive) {
          const cameraProgress = easeOutCubic(galagaProgress);
          const galagaPlayer = galaga?.player;
          if (galagaPlayer?.view === "cockpit") {
            const boostPull = galagaPlayer.boosting ? -1.8 : 0;
            camera.position.set(
              lerp(0, galagaPlayer.x * 0.62, cameraProgress),
              lerp(2, galagaPlayer.y + 1.05, cameraProgress),
              lerp(104, galagaPlayer.z + 11.8 + boostPull, cameraProgress),
            );
            camera.lookAt(galagaPlayer.x + galagaPlayer.roll * 0.7, galagaPlayer.y + 5.2, galagaPlayer.z - 12);
          } else {
            camera.position.set(
              lerp(0, (galagaPlayer?.x ?? 0) * 0.12, cameraProgress),
              lerp(2, -1.35, cameraProgress),
              lerp(104, 34, cameraProgress),
            );
            camera.lookAt((galagaPlayer?.x ?? 0) * 0.14, 0.65, -9.6);
          }
        } else {
          camera.position.set(
            lerp(0, activeCameraX, timeline.arrival),
            lerp(2, activeCameraY, timeline.arrival),
            lerp(104, activeCameraZ, timeline.arrival),
          );
          camera.lookAt(focusX, focusY, focusZ);
        }
        const hudState = createHudState(gameplay, timeline.phase);
        const selectedHudTurret = hudState.turretOptions.find((turret) => turret.index === hudState.selectedTurretIndex);
        runtimeRef.current.snapshot = {
          mode: runtimeRef.current.galagaActive ? "galaga" : runtimeRef.current.gameActive ? "game" : "splash",
          coordinateSystem:
            "grid origin is top-left; grid x increases right; grid y increases down; Three.js maps grid x to world x and grid y to world z",
          transition: Number((runtimeRef.current.galagaActive ? galagaProgress : timeline.progress).toFixed(3)),
          audio: isMusicPlaying ? "active" : "idle",
          worldBuild: {
            phase: timeline.phase,
            planes: Number(timeline.planes.toFixed(3)),
            floor: Number(timeline.floor.toFixed(3)),
            walls: Number(timeline.walls.toFixed(3)),
            orbs: Number(timeline.orbs.toFixed(3)),
            enemies: Number(timeline.enemies.toFixed(3)),
            galaga: Number(galagaProgress.toFixed(3)),
          },
          visualizer: {
            ballShape: isMusicPlaying ? "audio-reactive" : "round",
            gridMotion: runtimeRef.current.galagaActive
              ? "star-tunnel"
              : runtimeRef.current.gameActive && timeline.planes >= 1
                ? "flat"
                : isMusicPlaying
                  ? "audio-reactive"
                  : "synthetic",
          },
          camera: {
            zoom: Number(runtimeRef.current.zoom.toFixed(3)),
            zoomTarget: Number(runtimeRef.current.zoomTarget.toFixed(3)),
            lensZoom: Number(camera.zoom.toFixed(3)),
            yaw: Number(runtimeRef.current.yaw.toFixed(3)),
            yawTarget: Number(runtimeRef.current.yawTarget.toFixed(3)),
            pitch: Number(runtimeRef.current.pitch.toFixed(3)),
            pitchTarget: Number(runtimeRef.current.pitchTarget.toFixed(3)),
            focusX: Number(runtimeRef.current.focusX.toFixed(2)),
            focusZ: Number(runtimeRef.current.focusZ.toFixed(2)),
            focusTargetX: Number(runtimeRef.current.focusTargetX.toFixed(2)),
            focusTargetZ: Number(runtimeRef.current.focusTargetZ.toFixed(2)),
            projection: runtimeRef.current.projection,
            position: {
              x: Number(camera.position.x.toFixed(2)),
              y: Number(camera.position.y.toFixed(2)),
              z: Number(camera.position.z.toFixed(2)),
            },
            perspective:
              runtimeRef.current.projection === "top" || runtimeRef.current.projection === "bottom"
                ? runtimeRef.current.projection
                : overviewZoom > 0.65
                  ? "overview"
                  : "battlefield",
          },
          gameplay: {
            status: hudState.status,
            wave: hudState.wave,
            processedWave: hudState.processedWave,
            energy: Math.round(hudState.energy),
            score: Math.round(hudState.score),
            scoreBonus: Math.round(hudState.scoreBonus),
            timeRemaining: Number(hudState.timeRemaining.toFixed(2)),
            orbsAlive: hudState.orbsAlive,
            totalOrbs: hudState.totalOrbs,
            enemies: hudState.enemies,
            spawners: hudState.spawners,
            turretsPlaced: hudState.turretsPlaced,
            placeableTurretCount: hudState.turretOptions.length,
            upgradeCatalogCount: ORB_DEFENSE_TURRETS.length,
            selectedTurret: hudState.selectedTurretLabel,
            selectedTurretCost: hudState.selectedTurretCost,
            selectedTurretDetail: selectedHudTurret
              ? {
                  name: selectedHudTurret.name,
                  level: selectedHudTurret.levelLabel,
                  power: selectedHudTurret.power,
                  range: selectedHudTurret.rangeLabel,
                  size: selectedHudTurret.sizeLabel,
                  cost: selectedHudTurret.costLabel,
                  efficiency: selectedHudTurret.efficiency,
                  purchaseRequirement: selectedHudTurret.purchaseRequirement,
                }
              : null,
            message: hudState.message,
            lastPointer: gameplay?.lastPointer ?? null,
            placementPreview: gameplay?.hoverPreview
              ? {
                  mode: gameplay.hoverPreview.mode,
                  valid: gameplay.hoverPreview.valid,
                  gridX: gameplay.hoverPreview.point.x,
                  gridY: gameplay.hoverPreview.point.y,
                  width: gameplay.hoverPreview.w,
                  height: gameplay.hoverPreview.h,
                  currentRange: gameplay.hoverPreview.currentRange,
                  nextRange: gameplay.hoverPreview.nextRange,
                }
              : null,
            shots: gameplay?.shots.length ?? 0,
            visibleShots:
              gameplay?.shots.slice(0, 6).map((shot) => ({
                color: `#${shot.color.toString(16).padStart(6, "0")}`,
                ageFrames: shot.ageFrames,
                lifeFrames: shot.lifeFrames,
              })) ?? [],
            rewardBursts: gameplay?.rewardBursts.length ?? 0,
          },
          galaga: galaga ? serializeGalagaState(galaga) : null,
          resources: activeGameScene
            ? {
                energy: {
                  displayedValue: Math.round(activeGameScene.resourceAnchors.energy.displayedValue),
                  text: activeGameScene.resourceAnchors.energy.valueText,
                  coreScale: Number(activeGameScene.resourceAnchors.energy.core.scale.x.toFixed(3)),
                  target: {
                    x: Number(activeGameScene.resourceAnchors.energy.target.x.toFixed(2)),
                    y: Number(activeGameScene.resourceAnchors.energy.target.y.toFixed(2)),
                    z: Number(activeGameScene.resourceAnchors.energy.target.z.toFixed(2)),
                  },
                },
                score: {
                  displayedValue: Math.round(activeGameScene.resourceAnchors.score.displayedValue),
                  text: activeGameScene.resourceAnchors.score.valueText,
                  coreScale: Number(activeGameScene.resourceAnchors.score.core.scale.x.toFixed(3)),
                  target: {
                    x: Number(activeGameScene.resourceAnchors.score.target.x.toFixed(2)),
                    y: Number(activeGameScene.resourceAnchors.score.target.y.toFixed(2)),
                    z: Number(activeGameScene.resourceAnchors.score.target.z.toFixed(2)),
                  },
                },
                rewardParticles: activeGameScene.rewardParticles.length,
              }
            : null,
          terrain: {
            flattened: Number((runtimeRef.current.galagaActive ? galagaProgress : timeline.planes).toFixed(3)),
            waveScale: Number(waveScale.toFixed(3)),
            gridScale: Number(gridScale.toFixed(3)),
          },
          grid: {
            rows: ORB_DEFENSE_GRID_HEIGHT,
            columns: ORB_DEFENSE_GRID_WIDTH,
            pathCells: activeGameScene?.counts[0] ?? GRID_COUNTS[0],
            wallCells: activeGameScene?.counts[1] ?? GRID_COUNTS[1],
            playerSpaceCells: activeGameScene?.counts[2] ?? GRID_COUNTS[2],
          },
          targets: ORB_TARGETS,
          spawns: SPAWN_LOCATIONS,
          visibleEnemies:
            gameplay?.enemies
              .slice(0, 8)
              .map((enemy) => ({
                id: enemy.id,
                kind: enemy.kind,
                label: enemy.label,
                gridX: Number(enemy.position.x.toFixed(2)),
                gridY: Number(enemy.position.y.toFixed(2)),
                health: Math.max(0, Math.round(enemy.health)),
                pathIndex: enemy.pathIndex,
              })) ?? [],
        };

        const nowSeconds = performance.now() / 1000;
        if (runtimeRef.current.gameActive && nowSeconds - runtimeRef.current.lastHudPublishAt > 0.14) {
          runtimeRef.current.lastHudPublishAt = nowSeconds;
          window.dispatchEvent(new CustomEvent<OrbDefenseHudState>(ORB_DEFENSE_STATE_EVENT, { detail: hudState }));
        }

        renderer?.render(scene, camera);

        if (!reducedMotion) {
          window.cancelAnimationFrame(frame);
          frame = window.requestAnimationFrame(render);
        }
      };

      renderRef.current = render;
      window.advanceTime = (ms: number) => {
        runtimeRef.current.manualElapsed = (runtimeRef.current.manualElapsed ?? performance.now() / 1000) + ms / 1000;
        render();
      };
      window.render_game_to_text = () => JSON.stringify(runtimeRef.current.snapshot);
      window.render_galaga_to_text = () => JSON.stringify(galagaRef.current ? serializeGalagaState(galagaRef.current) : null);

      resize();
      window.addEventListener("resize", resize);
      window.addEventListener("keydown", handleKeyDown);
      window.addEventListener("keyup", handleKeyUp);
      document.addEventListener("fullscreenchange", resize);
      canvas?.addEventListener("wheel", handleWheel, { passive: false });
      canvas?.addEventListener("pointerdown", handlePointerDown, { passive: false });
      canvas?.addEventListener("pointermove", handlePointerMove, { passive: false });
      canvas?.addEventListener("pointerup", handlePointerUp, { passive: false });
      canvas?.addEventListener("pointercancel", handlePointerCancel);
      canvas?.addEventListener("pointerleave", handlePointerLeave);
      canvas?.addEventListener("contextmenu", handleContextMenu);
      render();

      cleanup = () => {
        window.cancelAnimationFrame(frame);
        window.removeEventListener("resize", resize);
        window.removeEventListener("keydown", handleKeyDown);
        window.removeEventListener("keyup", handleKeyUp);
        document.removeEventListener("fullscreenchange", resize);
        canvas?.removeEventListener("wheel", handleWheel);
        canvas?.removeEventListener("pointerdown", handlePointerDown);
        canvas?.removeEventListener("pointermove", handlePointerMove);
        canvas?.removeEventListener("pointerup", handlePointerUp);
        canvas?.removeEventListener("pointercancel", handlePointerCancel);
        canvas?.removeEventListener("pointerleave", handlePointerLeave);
        canvas?.removeEventListener("contextmenu", handleContextMenu);
        if (window.advanceTime) {
          delete window.advanceTime;
        }
        if (window.render_game_to_text) {
          delete window.render_game_to_text;
        }
        if (window.render_galaga_to_text) {
          delete window.render_galaga_to_text;
        }
        renderRef.current = null;
        planeStates.forEach(({ mesh }) => {
          mesh.geometry.dispose();
          mesh.material.dispose();
        });
        ball.geometry.dispose();
        ball.material.dispose();
        if (gameScene) {
          gameScene.geometries.forEach((geometry) => geometry.dispose());
          Array.from(new Set(gameScene.materials)).forEach(disposeMaterial);
        }
        if (galagaScene) {
          galagaScene.geometries.forEach((geometry) => geometry.dispose());
          Array.from(new Set(galagaScene.materials)).forEach(disposeMaterial);
        }
        renderer?.dispose();
        renderer?.domElement.remove();
      };
    } catch {
      setWebglMode("unsupported");
    }

    return cleanup;
  }, [reducedMotion, useStaticBackdrop]);

  useEffect(() => stopAudio, [stopAudio]);

  return (
    <button
      type="button"
      className="audio-three-background"
      aria-label={audioState.isPlaying ? "Pause background audio visualizer" : "Start background audio visualizer"}
      data-audio={audioState.isPlaying ? "active" : "idle"}
      data-game={gameActive ? "active" : "splash"}
      data-webgl={webglMode}
      data-testid="audio-three-background"
      onClick={() => {
        if (suppressAudioClickRef.current) {
          suppressAudioClickRef.current = false;
          return;
        }
        togglePlayback();
      }}
    >
      <span ref={hostRef} className="audio-three-stage" aria-hidden="true" />
    </button>
  );
}

function averageByte(values: Uint8Array<ArrayBuffer>) {
  return values.reduce((sum, value) => sum + value, 0) / Math.max(1, values.length);
}

function peakByte(values: Uint8Array<ArrayBuffer>) {
  return values.length > 0 ? Math.max(...values) : 0;
}

function rmsByte(values: Uint8Array<ArrayBuffer>) {
  if (values.length === 0) {
    return 0;
  }

  let sum = 0;
  for (let index = 0; index < values.length; index += 1) {
    const centered = (values[index] - 128) / 128;
    sum += centered * centered;
  }

  return Math.sqrt(sum / values.length);
}

function readAudioBands(audio: AudioNodes): AudioBands {
  audio.analyser.getByteFrequencyData(audio.data);
  audio.analyser.getByteTimeDomainData(audio.waveform);

  const split = Math.floor(audio.data.length / 2);
  const lowerRange = audio.data.slice(0, Math.max(1, split - 1));
  const upperRange = audio.data.slice(Math.max(1, split - 1), Math.max(1, audio.data.length - 1));
  const bassRange = audio.data.slice(0, 36);
  const midRange = audio.data.slice(36, 128);
  const lowerPeakFr = peakByte(lowerRange) / Math.max(1, lowerRange.length);
  const upperPeakFr = peakByte(upperRange) / Math.max(1, upperRange.length);
  const lowerAverageFr = averageByte(lowerRange) / Math.max(1, lowerRange.length);
  const upperAverageFr = averageByte(upperRange) / Math.max(1, upperRange.length);
  const bassAverageFr = averageByte(bassRange) / Math.max(1, bassRange.length);
  const midAverageFr = averageByte(midRange) / Math.max(1, midRange.length);
  const bassPeakRaw = peakByte(bassRange) / 255;
  const bassAverageRaw = averageByte(bassRange) / 255;
  const loudness = rmsByte(audio.waveform);

  const rawBands: AudioBands = {
    bass: clamp(Math.pow(lowerPeakFr, 0.8) + bassAverageFr * 0.32, 0.02, 2.4),
    mid: clamp(midAverageFr * 1.55 + lowerAverageFr * 0.25, 0.02, 2.4),
    treble: clamp(upperAverageFr * 1.7 + upperPeakFr * 0.45, 0.02, 2.4),
    energy: clamp((lowerPeakFr + upperAverageFr + bassAverageFr) / 1.55 + loudness * 1.6, 0.02, 2.4),
    peak: clamp(Math.max(lowerPeakFr, upperPeakFr), 0.02, 2.4),
    impact: clamp(loudness * 8.5 + Math.pow(lowerPeakFr, 0.85) * 0.3, 0.02, 1.6),
    bassImpact: clamp(Math.pow(bassPeakRaw, 0.82) * 1.25 + bassAverageRaw * 0.72, 0.02, 1.7),
  };

  const previous = audio.bands;
  const bassAttack = rawBands.bass > previous.bass ? 0.3 : 0.14;
  const energyAttack = rawBands.energy > previous.energy ? 0.24 : 0.12;
  const impactAttack = rawBands.impact > previous.impact ? 0.64 : 0.2;
  const bassImpactAttack = rawBands.bassImpact > previous.bassImpact ? 0.74 : 0.18;
  audio.bands = {
    bass: lerp(previous.bass, rawBands.bass, bassAttack),
    mid: lerp(previous.mid, rawBands.mid, 0.12),
    treble: lerp(previous.treble, rawBands.treble, 0.12),
    energy: lerp(previous.energy, rawBands.energy, energyAttack),
    peak: lerp(previous.peak, rawBands.peak, 0.16),
    impact: lerp(previous.impact, rawBands.impact, impactAttack),
    bassImpact: lerp(previous.bassImpact, rawBands.bassImpact, bassImpactAttack),
  };

  return audio.bands;
}
